import air from './rate-management-air'
import express from './rate-management-express'
import lcl from './rate-management-lcl'

export default {
  // Rate Management
  rateManagement: {
    // Module titles and descriptions
    title: 'Rate Management',
    description: 'Manage various rate information',
    priceDisplay: {
      mode: 'Price Display',
      numeric: 'Numeric',
      currencyStr: 'Multi-Currency'
    },
    common: {
      selectAllMatched: 'Select All ({count})',
      deselectAllMatched: 'Deselect All ({count})',
      noDataToSelect: 'No records to select for the current query',
      selectAllMustUnpublished: 'Some records cannot be published. Only draft and pending-import rates can be batched online.',
      selectAllMustPublished: 'Some records cannot be offlined. Only published rates can be batched offline.',
      selectAllMustDraft: 'Some records cannot be deleted. Only draft rates can be batched deleted.',
      onlineConfirmTitle: 'Confirm Online',
      onlineConfirmContent: 'Publish the selected {count} rates?'
    },
    // Batch modify (shared drawer texts for air / lcl / preport / lastmile)
    batchModify: {
      title: 'Batch Modify',
      selectedCount: '{count} rates selected',
      filterScope: 'Current filter results',
      reminder: 'Only unpublished rates (status not "published") can be modified; published rates will be skipped automatically. Unfilled fields remain unchanged; for text fields, selecting "Clear" will remove the original value.',
      confirm: 'Confirm',
      cancel: 'Cancel',
      selectPlaceholder: 'Please select',
      inputPlaceholder: 'Please input',
      modify: 'Set to',
      clearOption: 'Clear',
      noChange: 'Please fill in at least one field to modify',
      invalidDate: 'Start date cannot be later than end date',
      success: 'Batch modification succeeded: {count} records',
      partialSuccess: 'Batch modification completed: {success} succeeded, {skipped} skipped, {failed} failed',
      failed: 'Batch modification failed: {failed} records',
      noData: 'No matching unpublished rate records found',
      networkError: 'Batch modification request failed, please try again later',
      fields: {
        validDate: 'Validity',
        priceTrend: 'Price Trend',
        specialPriceStatus: 'Special Price Status',
        vessel: 'Vessel',
        voyage: 'Voyage',
        voyageDays: 'Voyage Days',
        remarks: 'Remarks',
        receivingInstructions: 'Receiving Instructions'
      },
      options: {
        priceTrend: {
          1: 'Up',
          2: 'Down',
          3: 'Stable'
        },
        specialPriceStatus: {
          1: 'Not allowed',
          2: 'Allowed',
          3: 'Special'
        }
      }
    },
    rail: {
      addTitle: 'Create Rail Rate',
      viewTitle: 'Rail Rate Details',
      editTitle: 'Edit Rail Rate',
      actions: {
        add: 'New Rate',
        import: 'Import',
        export: 'Export',
        batchOperations: 'Batch Operations',
        batchPublish: 'Batch Publish',
        batchDisable: 'Batch Disable',
        batchEnable: 'Batch Enable',
        downloadTemplate: 'Download Template',
        view: 'Details',
        edit: 'Edit',
        more: 'More',
        copy: 'Copy',
        publish: 'Publish',
        disable: 'Disable',
        enable: 'Enable'
      },
      columns: {
        index: 'No.',
        priceNo: 'Rate Code',
        companyIds: 'Company',
        supplier: 'Supplier',
        railCompany: 'Rail Company',
        pol: 'Origin Station',
        pod: 'Destination Station',
        routeType: 'Route',
        containerOwner: 'Container Owner',
        cargoType: 'Cargo Type',
        transportMode: 'Transport Mode',
        transportTerms: 'Transport Terms',
        directTransit: 'Direct/Transit',
        countryOfDeparture: 'Departure Country',
        countryOfDestination: 'Destination Country',
        exitPort: 'Exit Port',
        costPrice: 'Cost Price',
        sellPrice: 'Selling Price',
        hsCode: 'HS Code',
        weightLimit: 'Weight Limit(t)',
        freeDemurrage: 'Free Demurrage(d)',
        validityDate: 'Validity',
        status: 'Status',
        action: 'Actions'
      },
      messages: {
        loadFailed: 'Failed to load rail rates',
        containerOwnerLoadFailed: 'Failed to load container owner options. Please try again later.',
        exportSubmitFailed: 'Failed to submit export task',
        exportSubmitted: 'Export task submitted',
        exportFailed: 'Export failed',
        confirmExportTitle: 'Confirm Export',
        confirmExportSelected:
          'Export the selected {count} rail rates. The task will run in the background and can be downloaded from Task Center.',
        confirmExportAll:
          'Export rail rates by current filters. The task will run in the background and can be downloaded from Task Center.',
        goTaskCenterTitle: 'Go to Task Center',
        goTaskCenterContent: 'Go to Task Center to view export progress?',
        goTaskCenterOk: 'Go to Task Center',
        confirm: 'Confirm',
        cancel: 'Cancel',
        publishSuccess: 'Published successfully',
        disableSuccess: 'Disabled successfully',
        enableSuccess: 'Enabled successfully',
        copySuccess: 'Rate information copied. Confirm and save.',
        templateDownloading: 'Downloading template',
        templateDownloaded: 'Template downloaded',
        templateDownloadFailed: 'Failed to download template',
        publishFailed: 'Failed to publish',
        disableFailed: 'Failed to disable',
        enableFailed: 'Failed to enable',
        batchPublishFailed: 'Failed to batch publish',
        batchDisableFailed: 'Failed to batch disable',
        batchEnableFailed: 'Failed to batch enable'
      },
      validation: {
        selectRailRate: 'Please select rail rates',
        publishDraftOnly: 'Only draft rail rates can be published',
        disablePublishedOnly: 'Only published rail rates can be disabled',
        enableDisabledOnly: 'Only disabled rail rates can be enabled',
        versionMissing: 'Version is missing. Refresh and try again.'
      },
      import: {
        title: 'Import Rail Rates',
        template: 'Template',
        downloadTemplate: 'Download Rail Rate Template',
        instructionTitle: 'Import Instructions',
        instruction1:
          '1. Supports importing one Excel file. A task will be created and processed in Task Management.',
        instruction2: '2. Master data fields can use IDs, names, or codes that can be searched.',
        instruction3: '3. Check Task Management for import results and failed row reasons.',
        uploadText: 'Click or drag file here',
        uploadHint: 'Supports .xlsx / .xls',
        reselect: 'Reselect',
        cancel: 'Cancel',
        startImport: 'Start Import',
        downloadFailed: 'Failed to download template',
        taskCreated: 'Import task created. Check Task Management for results.',
        taskCreateFailed: 'Failed to create import task'
      },
      detail: {
        back: 'Back',
        title: 'Rail Rate',
        surchargeTitle: 'Surcharge',
        boxTypeSetting: 'Container Type Settings',
        add: 'Add',
        feeName: 'Fee Name',
        selectFee: 'Select fee',
        unit: 'Unit',
        currency: 'Currency',
        paymentType: 'Payment Type',
        paymentTypePlaceholder: 'Type',
        prepaid: 'Prepaid',
        collect: 'Collect',
        minCharge: 'Min Charge',
        maxCharge: 'Max Charge',
        feeSource: 'Fee Source',
        systemRule: 'System Rule',
        manualCreate: 'Manual',
        unitPrice: 'Unit Price',
        alreadyIncluded: 'Already Included in Total',
        includeInTotal: 'Include in Total',
        remarks: 'Remarks',
        optional: 'Optional',
        action: 'Actions',
        boxTypeUpdated: 'Container type settings updated',
        surchargeMatchFailed: 'Failed to match surcharge automatically',
        validPeriodRequired: 'Please select validity',
        versionMissing: 'Version is missing. Refresh and try again.',
        surchargeContainerPriceRequired:
          'Surcharge row {index}: enter at least one container price',
        surchargeUnitPriceRequired: 'Surcharge row {index}: enter unit price'
      },
      statusText: {
        draft: 'Draft',
        published: 'Published',
        expired: 'Expired',
        publishFailed: 'Publish Failed',
        disabled: 'Disabled',
        pendingImport: 'Pending Import'
      },
      display: {
        railwayBox: 'Railway Box',
        inlandPortBox: 'Inland Port Box',
        crBox: 'CR Box',
        nonCrBox: 'Non-CR Box',
        middleLine: 'Middle Line',
        northLine: 'North Line',
        southLine: 'South Line',
        southeastAsia: 'Southeast Asia',
        centralAsia: 'Central Asia'
      },
      options: {
        direction: {
          outbound: 'Outbound',
          inbound: 'Inbound'
        },
        routeType: {
          middle: 'Middle',
          north: 'North',
          south: 'South',
          southeast: 'Southeast Asia',
          centralAsia: 'Central Asia'
        },
        containerOwner: {
          coc: 'COC',
          soc: 'SOC',
          railwayBox: 'Railway Box',
          inlandPortBox: 'Inland Port Box',
          crBox: 'CR Box',
          nonCrBox: 'Non-CR Box'
        },
        directTransit: {
          direct: 'Direct',
          transit: 'Transit'
        },
        transportMode: {
          container: 'Container',
          truck: 'Truck',
          lcl: 'LCL'
        },
        paymentMethod: {
          prepaid: 'Prepaid',
          collect: 'Collect'
        }
      },
      form: {
        basicInfo: 'Basic Info',
        priceNo: 'Rate Code',
        autoGenerate: 'Auto Generated',
        company: 'Company',
        supplier: 'Supplier',
        railCompany: 'Rail Company',
        direction: 'Direction',
        pickupCity: 'Pickup Place',
        routeType: 'Route Type',
        containerOwner: 'Container Owner',
        cargoType: 'Cargo Type',
        cityOfDeparture: 'Departure City',
        cityOfDestination: 'Destination City',
        countryOfDeparture: 'Departure Country',
        countryOfDestination: 'Destination Country',
        pol: 'Origin Station',
        pod: 'Destination Station',
        exitPort: 'Exit Port',
        entryPort: 'Entry Port',
        hsCode: 'HS Code',
        weightLimit: 'Weight Limit (Ton)',
        freeDemurrage: 'Free Demurrage Days',
        transportTerms: 'Transport Terms',
        directTransit: 'Direct/Transit',
        validPeriod: 'Validity',
        transitStations: 'Transit Stations',
        remarks: 'Remarks',
        status: 'Rate Status',
        specialPriceStatus: 'Special Price Status',
        specialPriceStatusPlaceholder: 'Select special price status',
        specialPriceStatusDisallow: 'Special price not allowed',
        specialPriceStatusAllow: 'Special price allowed',
        specialPriceStatusSpecial: 'Special price',
        weekdays: 'Sailing Days',
        onePrice: 'One-Price',
        receivingInstructions: 'Receiving Instructions',
        trainDepartureTime: 'Train Departure Time',
        borderCrossTime: 'Border Crossing Time',
        trainArrivalTime: 'Train Arrival Time',
        weekdaysPlaceholder: 'Select sailing days',
        onePricePlaceholder: 'Select',
        receivingInstructionsPlaceholder: 'Enter receiving instructions',
        trainDepartureTimePlaceholder: 'Select train departure time',
        borderCrossTimePlaceholder: 'Select border crossing time',
        trainArrivalTimePlaceholder: 'Select train arrival time',
        transportMode: 'Transport Mode',
        truckFreight: 'Truck Freight',
        railFreight: 'Rail Freight',
        lclFreight: 'LCL Rate',
        volumePricing: 'Volume',
        setRange: 'Set Range',
        addRow: 'Add Row',
        add: 'Add',
        truckType: 'Truck Type',
        stdWeight: 'Standard Weight',
        unit: 'Unit',
        basePrice1: 'Base Price 1',
        basePrice2: 'Base Price 2',
        unitPrice: 'Unit Price',
        sellPrice: 'Selling Price',
        currency: 'Currency',
        paymentMethod: 'Payment Method',
        minPrice: 'Min Price',
        maxPrice: 'Max Price',
        action: 'Actions',
        containerType: 'Container Type',
        volumeRangeSetting: 'Volume Range Settings',
        volumeRangeTip:
          'Configure volume tiers. Each tier maps to one price matrix column and can be added or removed.',
        tier: 'Tier',
        tierDisplay: 'Tier {index}',
        startInclude: 'Start (Included)',
        endExclude: 'End (Excluded)',
        display: 'Display',
        addTier: 'Add Tier',
        select: 'Select',
        input: 'Enter',
        optional: 'Optional',
        startDate: 'Start Date',
        endDate: 'End Date',
        noLimit: 'Leave empty for no limit',
        transitStationsPlaceholder: 'Separate stations with commas or semicolons',
        transitStationsTip:
          'Required. Multiple stations are supported, separated by commas or semicolons.',
        hsCodePlaceholder: 'Separate multiple codes with commas',
        weightPlaceholder: 'e.g. 20',
        dayPlaceholder: 'Days',
        truckTypePlaceholder: 'e.g. Gondola',
        stdWeightPlaceholder: 'e.g. 60t',
        emptyTruckRows: 'No data. Click Add Row to create one.',
        emptyLclRows: 'No LCL rates. Click Add to create one.',
        emptyMatrixRows: 'No price records. Click Add Row to create one.',
        volumeStartRequired: 'Volume start must be greater than or equal to 0',
        volumeEndGreaterThanStart: 'Volume end must be greater than start',
        volumeRangeOverlap: 'Volume ranges cannot overlap',
        volumeNoLimitLast: 'Open-ended volume range must be the last one',
        volumeApplied: 'Volume ranges applied and will take effect after saving',
        previousVolumeEndRequired: 'Set the previous range end first',
        keepOneVolumeRange: 'Keep at least one range',
        companyRequired: 'Please select company',
        railCompanyRequired: 'Please select rail company',
        polRequired: 'Please select origin station',
        podRequired: 'Please select destination station',
        stationCountryLoadFailed: 'Unable to load station country information. Please select the station again and retry.',
        directionRequired: 'Please select direction',
        routeTypeRequired: 'Please select route type',
        containerOwnerRequired: 'Please select container owner',
        cargoTypeRequired: 'Please select cargo type',
        transportModeRequired: 'Please select transport mode',
        transportTermsRequired: 'Please select transport terms',
        directTransitRequired: 'Please select direct/transit',
        exitPortRequired: 'Please select exit port',
        pickupCityRequired: 'Pickup place is required for outbound direction',
        dangerousHsCodeRequired: 'HS code is required for dangerous cargo',
        hsCodeTooLong: 'HS code cannot exceed 50 characters',
        remarksTooLong: 'Remarks cannot exceed 500 characters',
        transitStationsRequired: 'Transit stations are required',
        validPeriodRequired: 'Please select validity',
        pickupCityWarning: 'Please enter pickup place for outbound direction',
        transitStationsWarning: 'Please enter transit stations',
        departureRequired: 'Enter at least one departure country, city, or station',
        destinationRequired: 'Enter at least one destination country, city, or station',
        truckRowsRequired: 'Maintain at least one truck rate',
        truckTypeRequired: 'Truck rate row {index}: enter truck type',
        stdWeightRequired: 'Truck rate row {index}: enter standard weight',
        truckUnitRequired: 'Truck rate row {index}: select unit',
        truckCurrencyRequired: 'Truck rate row {index}: select currency',
        truckVolumePriceRequired: 'Truck rate row {index}: enter at least one volume price',
        truckBasePriceRequired: 'Truck rate row {index}: enter base price 1 and base price 2',
        truckUnitPriceRequired: 'Truck rate row {index}: enter unit price',
        lclRowsRequired: 'Maintain at least one LCL rate',
        lclUnitRequired: 'LCL rate row {index}: select unit',
        lclCurrencyRequired: 'LCL rate row {index}: select currency',
        lclUnitPriceRequired: 'LCL rate row {index}: enter unit price',
        matrixRowsRequired: 'Maintain at least one rail rate price',
        matrixContainerRequired: 'Price matrix row {index}: select container type',
        matrixContainerDuplicate: 'Duplicate container type in price matrix: {container}',
        matrixCurrencyRequired: 'Price matrix row {index}: select currency',
        matrixVolumePriceRequired: 'Price matrix row {index}: enter at least one volume price',
        matrixUnitPriceRequired: 'Price matrix row {index}: enter unit price',
        matrixRemarksTooLong: 'Price matrix row {index}: remarks cannot exceed 100 characters'
      }
    },
    status: {
      draft: 'Draft',
      normal: 'Normal',
      published: 'Published',
      expired: 'Expired',
      offline: 'Offline'
    },

    // Tab navigation
    tabs: {
      ecommerce: 'E-commerce Rate',
      fcl: 'FCL Rate',
      lcl: 'LCL Rate',
      air: 'Air Rate',
      express: 'Express',
      preport: 'Pre-port Rate',
      lastmile: 'Last Mile Rate',
      rail: 'Rail Rate'
    },

    actions: {
      importTitle: 'Import Rates ({type})',
      clickToUpload: 'Click or drag file here to upload',
      uploadLimit: 'Supported format: Excel (.xlsx, .xls), max 10MB per file',
      downloadTemplate: 'Download Template',
      startImport: 'Start Import',
      downloadErrorReport: 'Download Error Report',
      close: 'Close',
      viewDrafts: 'View Drafts',
      batchPublishCount: 'Batch Publish ({count})'
    },

    messages: {
      importInstructions: 'Import Instructions:',
      importStep1:
        'The system will parse your uploaded Excel and automatically validate and match rules;',
      importStep2: 'Valid rows will be automatically saved to your draft list;',
      importStep3:
        'If conflicts or errors occur, a detailed error log report will be provided for your review.',
      uploading: 'Uploading and reading file...',
      validating: 'Validating data and checking master data links...',
      matching: 'Matching rules and charging items...',
      saving: 'Saving batch data into drafts...',
      processingWait: 'Data is being processed at high speed in background. Please wait...',
      importSuccess: 'Successfully Imported',
      conflictDetected: 'Conflicts / Duplicates Detected',
      validationFailed: 'Validation Failed',
      autoMatchResult: 'Smart rule auto-matching results:',
      rowsFailed: 'rows failed validation. Download error report for details.',
      downloadingTemplate: 'Downloading import template for you',
      generatingErrorReport: 'Generating error report...',
      batchPublishSuccess: 'Successfully published {count} imported drafts!',
      noValidTaskId: 'No valid task ID returned',
      importTaskSubmitted: 'Rate import task submitted, parsing in background',
      uploadFailed: 'Upload processing failed',
      noErrorDetails: 'No error details to export',
      errorReportExported: 'Error report exported successfully',
      confirmPublishFailed: 'Failed to confirm publish',
      errorReportTitle: 'Rate Import Error Report',
      errorReportLine: 'Row: {rows}, Field: {field}, Reason: {reason}'
    },

    fields: {
      markupMatched: 'Markup rules matched {count} items',
      surchargeMatched: 'Surcharge rules matched {count} items'
    },

    lcl,

    air,

    express,

    // FCL Rate Management
    fcl: {
      // Page titles
      title: 'FCL Rate Management',
      description: 'Manage FCL rate information',
      createTitle: 'New FCL Rate',
      editTitle: 'Edit FCL Rate',
      viewTitle: 'View FCL Rate Details',

      // Filters
      filters: {
        priceNo: 'Rate No.',
        batchNo: 'Batch No.',
        pricingNature: 'Offer Nature',
        batchNoPlaceholder: 'Enter batch no.',
        freightType: 'Rate Type',
        pol: 'POL',
        pod: 'POD',
        podRouteCategory: 'POD Route Category',
        por: 'Place of Receipt',
        pdl: 'Place of Delivery',
        pot: 'Transit Port',
        pot1: 'Transit Port (1st)',
        pot2: 'Transit Port (2nd)',
        pot3: 'Transit Port (3rd)',
        carrierId: 'Carrier',
        carrierCode: 'Carrier',
        contractNo: 'Contract No.',
        laneId: 'Route',
        laneCode: 'Route Code',
        directFlag: 'Direct/Transit',
        spaceStatus: 'Space Status',
        status: 'Rate Status',
        priceTrend: 'Price Trend',
        cargoType: 'Cargo Type',
        spaceNature: 'Space Nature',
        specialPriceStatus: 'Special Price Status',
        laneType: 'Lane Type',
        containerOwnership: 'Container Ownership',
        weekdays: 'Vessel Schedule',
        vessel: 'Vessel',
        voyage: 'Voyage',
        voyageDays: 'Voyage Days',
        beginDate: 'Effective Date',
        endDate: 'Expiry Date',
        etd: 'ETD',
        eta: 'ETA',
        dateCreated: 'Created Time',
        dateModified: 'Modified Time',
        detentionFreeDays: 'Free Detention Days',
        demurrageFreeDays: 'Free Demurrage Days',
        nac: 'NAC',
        remarks: 'Remarks',
        createdBy: 'Created By',
        modifiedBy: 'Modified By'
      },

      // Placeholders
      placeholders: {
        priceNo: 'Enter rate number',
        freightType: 'Select rate type',
        pol: 'Select POL',
        pod: 'Select POD',
        pot: 'Select transit port',
        podRouteCategory: 'Select POD route category',
        por: 'Select place of receipt',
        pdl: 'Select place of delivery',
        pot2: 'Select transit port (2nd)',
        pot3: 'Select transit port (3rd)',
        cutoffWeekdays: 'Select cutoff weekdays',
        unboxDelivery: 'Select unbox delivery',
        freightCurrency: 'Select currency',
        freightPaymentType: 'Select payment terms',
        batchNo: 'Enter batch ID',
        carrierId: 'Select carrier',
        carrierCode: 'Select carrier',
        contractNo: 'Select contract number',
        laneId: 'Select route',
        supplier: 'Select supplier',
        transportTerm: 'Select transport terms',
        tradeTerms: 'Select trade terms',
        laneCode: 'Enter route code',
        directFlag: 'Select direct/transit',
        spaceStatus: 'Select space status',
        status: 'Select rate status',
        priceTrend: 'Select price trend',
        cargoType: 'Select cargo type',
        spaceNature: 'Select space nature',
        specialPriceStatus: 'Select special price status',
        laneType: 'Select lane type',
        containerOwnership: 'Select container ownership',
        weekdays: 'Select vessel schedule',
        vessel: 'Enter vessel name',
        voyage: 'Enter voyage',
        voyageDays: 'Enter voyage days',
        detentionFreeDays: 'Enter free detention days',
        demurrageFreeDays: 'Enter free demurrage days',
        nac: 'Select NAC',
        beginDate: 'Select effective date range',
        endDate: 'Select expiry date range',
        etd: 'Select ETD range',
        eta: 'Select ETA range',
        remarks: 'Enter remarks',
        createdBy: 'Enter created by',
        dateCreated: 'Select created time range',
        modifiedBy: 'Enter modified by',
        dateModified: 'Select modified time range'
      },

      // Table columns
      columns: {
        priceNo: 'Rate No.',
        pricingNature: 'Offer Nature',
        freightType: 'Rate Type',
        por: 'Place of Receipt',
        pol: 'POL',
        pdl: 'PDL',
        pod: 'Port of Discharge (POD)',
        podRouteCategory: 'POD Route Category',
        batchNo: 'Batch No.',
        laneId: 'Route',
        laneCode: 'Route Code',
        pot: 'Transit Port',
        pot1: 'Transit Port (1st)',
        pot2: 'Transit Port (2nd)',
        pot3: 'Transit Port (3rd)',
        directFlag: 'Direct/Transit',
        directFlag2: 'Direct/Transit',
        carrierId: 'Carrier',
        contractNo: 'Contract No.',
        spaceStatus: 'Space Status',
        priceTrend: 'Price Trend',
        status: 'Status',
        cargoType: 'Cargo Type',
        spaceNature: 'Space Nature',
        specialPriceStatus: 'Special Price Status',
        laneType: 'Lane Type',
        containerOwnership: 'Container Ownership',
        shippingSchedule: 'Vessel Schedule',
        voyage: 'Voyage Days',
        vesselName: 'Vessel',
        voyageNo: 'Voyage',
        validPeriod: 'Valid Period',
        etd: 'ETD',
        eta: 'ETA',
        cutoffDate: 'Closing Date',
        beginDate: 'Effective Date',
        endDate: 'Expiry Date',
        createDate: 'Create Date',
        modifyDate: 'Modify Date',
        freeStorageDays: 'Free Container',
        demurrageFreeDays: 'Free Demurrage',
        nac: 'NAC',
        gp20: '20GP',
        gp40: '40GP',
        hc20: '20HC',
        hc40: '40HC',
        hc45: '45HC',
        nor20: '20NOR',
        nor40: '40NOR',
        tk20: '20TK',
        tk40: '40TK',
        ot20: '20OT',
        ot40: '40OT',
        fr20: '20FR',
        fr40: '40FR',
        hq40: '40HQ',
        hq45: '45HQ',
        rf20: '20RF',
        rf40: '40RF',
        cost: 'Cost',
        sell: 'Selling Price',
        currency: 'Currency',
        destinationRegion: 'Destination Region',
        entryPerson: 'Created By',
        rateModifier: 'Modified By',
        rateStatus: 'Status',
        routeName: 'Route Name',
        routeCode: 'Route Code',
        terminal: 'Terminal',
        branch: 'Branch',
        supplier: 'Supplier',
        cargoDescription: 'Cargo Description',
        transportTerm: 'Transport Terms',
        tradeTerms: 'Trade Terms',
        polTerminal: 'POL Terminal',
        podTerminal: 'POD Terminal',
        cutoffWeekdays: 'Cutoff Weekdays',
        unboxDelivery: 'Unbox Delivery',
        weightLimit: 'Weight Limit',
        receivingInstructions: 'Receiving Instructions',
        onePrice: 'One Price',
        freightCurrency: 'Freight Currency',
        freightPaymentType: 'Payment Terms',
        remarks: 'Remarks',
        dateCreated: 'Created Time',
        actions: 'Actions'
      },

      // Weekdays
      weekdays: {
        monday: 'Mon',
        tuesday: 'Tue',
        wednesday: 'Wed',
        thursday: 'Thu',
        friday: 'Fri',
        saturday: 'Sat',
        sunday: 'Sun'
      },

      // Action buttons
      actions: {
        add: 'New FCL Rate',
        import: 'Import',
        importTooltip: 'Only supports the system-specified template format',
        export: 'Export',
        downloadTemplate: 'Download Template',
        aiParsing: 'AI Parsing',
        aiParsingTooltip: 'Supports any format; AI automatically recognizes and parses the data',
        aiRecognition: 'AI Recognition',
        batchModify: 'Batch Modify',
        batchOperations: 'Batch Operations',
        batchOnShelf: 'Batch Online',
        batchOffShelf: 'Batch Offline',
        batchPriceChange: 'Batch Price Change',
        batchValidityChange: 'Batch Time Change',
        batchDelete: 'Batch Delete',
        view: 'Details',
        edit: 'Edit',
        more: 'More',
        copy: 'Copy',
        onShelf: 'Online',
        offShelf: 'Offline',
        delete: 'Delete'
      },

      // Enums
      enums: {
        rateTypes: {
          contract: 'Contract Price',
          spot: 'SPOT E-commerce',
          peer: 'Peer Rate',
          ratio: 'Ratio Price'
        },
        transitTypes: {
          direct: 'Direct',
          transit: 'Transit'
        },
        spaceStatus: {
          smooth: 'Smooth',
          normal: 'Normal',
          singleApply: 'Single Apply',
          overbooked: 'Overbooked',
          notAccepting: 'Not Accepting',
          limited: 'Limited',
          suspended: 'Suspended',
          inquiry: 'Inquiry'
        },
        priceTrends: {
          rising: 'Price Rising',
          falling: 'Price Falling',
          stable: 'Price Stable'
        },
        status: {
          draft: 'Draft',
          normal: 'Normal',
          published: 'Published',
          expired: 'Expired',
          invalid: 'Invalid',
          offline: 'Offline',
          disabled: 'Disabled',
          pendingImport: 'Pending Import'
        },
        cargoTypes: {
          general: 'General',
          dangerous: 'Dangerous',
          frozen: 'Frozen',
          special: 'Special',
          steelCoil: 'Steel Coil',
          liquid: 'Liquid',
          chemical: 'Chemical',
          textile: 'Textile'
        }
      },
      status: {
        draft: 'Draft',
        normal: 'Normal',
        expired: 'Expired',
        offline: 'Offline'
      },
      confirm: {
        onShelf: 'Confirm Online',
        offShelf: 'Confirm Offline',
        delete: 'Confirm Delete',
        onShelfContent: 'Are you sure to online this rate?',
        offShelfContent: 'Are you sure to offline this rate?',
        deleteContent: 'Are you sure to delete this rate?'
      },

      // Batch operations
      batch: {
        selected: 'Selected {count} items',
        onShelfConfirm: 'Are you sure to online the selected {count} rates?',
        offShelfConfirm: 'Are you sure to offline the selected {count} rates?',
        deleteConfirm: 'Are you sure to delete the selected {count} rates? This cannot be undone.',
        onShelfSuccess: 'Online {count} rates successfully',
        offShelfSuccess: 'Offline {count} rates successfully',
        deleteSuccess: 'Delete {count} rates successfully',

        // Batch price change
        priceChange: {
          title: 'Batch Price Change',
          selectedRates: 'Selected {count} rates',
          operationReminder: 'Operation Reminder',
          reminderContent:
            "This operation will modify fee amounts for all selected rates. Please proceed with caution. You can select specific fee types for batch modification. Blank means no change for that box type. If the original rate doesn't have the corresponding box type price, the adjustment won't take effect. Please select increase or decrease first, and input positive integers only.",
          selectCharge: 'Select Fee',
          selectChargeDesc:
            'Select fee type for batch price modification, e.g. Ocean Freight, Handling Fee, etc.',
          selectChargePlaceholder: 'Select fee type',
          selectPriceMode: 'Select mode',
          priceIncrease: 'Increase',
          priceDecrease: 'Decrease',
          setPriceMode: 'Fixed Amount',
          priceIncreaseDesc: 'Select increase mode and enter amount (positive integer)',
          priceDecreaseDesc: 'Select decrease mode and enter amount (positive integer)',
          setPriceDesc: 'Select fixed mode and enter price for any box type (positive integer)',
          setPrice: 'Set Price',
          selectChargeFirst: 'Please select fee type first, then set price',
          rateListToModify: 'Rates to be modified',
          rateDetailsList: 'Rates list',
          rateNo: 'Rate No.',
          pol: 'POL',
          pod: 'POD',
          carrier: 'Carrier',
          itemCount: 'Rate Count',
          status: 'Status',
          showChargePrice: '(Showing {charge} price)',
          showAllCharges: '(Showing total price of all box-based fees)',
          noData: 'No Data',
          selectedSummary: 'Total {count} rates selected. Batch modifying prices for {type}.',
          currentChargeType: 'Current fee type:',
          confirmOperation: 'This operation will modify the',
          confirmOperationSuffix: 'amount for selected rates. Please confirm data accuracy.',
          selectChargeAndPrice: 'Please select fee type and set price first.',
          confirm: 'Confirm',
          cancel: 'Cancel',
          batchPriceSuccess: 'Batch price change successful',
          batchPriceFailed: 'Batch price change failed',
          batchPriceError: 'Batch price change error',
          pricePlaceholders: {
            increase: 'Increase amount',
            decrease: 'Decrease amount',
            setPrice: 'Fixed price',
            default: 'Enter price'
          },
          freightInfo: 'Ocean Freight Info',
          freightCurrency: 'Currency',
          freightCurrencyPlaceholder: 'e.g. USD',
          freightPaymentType: 'Payment Terms',
          freightPaymentTypePlaceholder: 'Select',
          freightEqPrice: 'Freight Container Prices',
          freightInfoDesc:
            'Leave blank to keep unchanged. Prices will overwrite existing ocean freight.',
          confirmFreightOperation:
            'This will update ocean freight info for selected rates. Please confirm.',
          selectChargeOrFreight:
            'Please select a fee type or fill in ocean freight info before confirming.'
        },

        // Unified batch modification
        modify: {
          title: 'Batch Modify',
          selectedRates: 'Selected {count} rates',
          filterScope: 'Current filtered results',
          reminder: 'Only unpublished rates (status not "published") can be modified; published rates will be skipped automatically. Blank ordinary fields remain unchanged; choosing Clear for a special field removes its existing value.',
          leftSection: 'Fees and Valid Period',
          rightSection: 'Ports and Sailing',
          costTitle: 'Cost Price (Base Ocean Freight)',
          costDesc: '+ Add to current price | - Subtract from current price | = Replace with adjustment amount',
          containerType: 'Container',
          operation: 'Action',
          adjustmentAmount: 'Adjustment Amount',
          amountPlaceholder: 'Please enter amount',
          keywordPlaceholder: 'Please enter keywords',
          inputPlaceholder: 'Please enter',
          selectPlaceholder: 'Please select',
          delete: 'Delete',
          addContainer: 'Add Container',
          confirm: 'Confirm',
          clearOption: 'Clear',
          freightEqPrice: 'Base Ocean Freight Cost',
          priceMode: 'Cost Price Operation',
          increase: 'Increase',
          decrease: 'Decrease',
          setPrice: 'Set to',
          pricePlaceholder: 'Blank means unchanged',
          validDate: 'Rate Valid Period',
          priceTrend: 'Price Trend',
          priceTrendPlaceholder: 'Select price trend',
          specialPriceStatus: 'Special Price Request',
          specialPricePlaceholder: 'Select special price status',
          transportTerm: 'Transport Terms',
          transportTermPlaceholder: 'Select transport terms',
          fieldOperation: 'Operation',
          modify: 'Modify to',
          clear: 'Clear',
          valuePlaceholder: 'Enter replacement value',
          transferPortInfo: 'Transit Port',
          receivingInstructions: 'Special Receiving Instructions',
          remarks: 'Remarks',
          overweightRemarks: 'Weight Limit',
          detentionFreeDays: 'Detention Free Days',
          demurrageFreeDays: 'Demurrage Free Days',
          por: 'Place of Receipt',
          pdl: 'Place of Delivery',
          pol: 'Port of Loading',
          polTerminalId: 'POL Terminal',
          pod: 'Port of Discharge',
          podTerminalId: 'POD Terminal',
          vessel: 'Vessel',
          voyage: 'Voyage',
          laneCode: 'Lane Code',
          voyageDays: 'Voyage Days',
          portPlaceholder: 'Select port',
          terminalPlaceholder: 'Select terminal',
          textPlaceholder: 'Blank means unchanged',
          numberPlaceholder: 'Enter a non-negative number',
          datePlaceholder: ['Start date', 'End date'],
          submit: 'Confirm Modify',
          cancel: 'Cancel',
          noChange: 'Please enter at least one field to modify',
          requiredValue: 'Enter a value after choosing Modify to',
          invalidDate: 'The start date cannot be later than the end date',
          duplicateContainer: 'Duplicate container types are not allowed',
          allContainersAdded: 'All available container types have been added',
          submitting: 'Batch modification in progress',
          success: 'Batch modification succeeded: {count} rates',
          partialSuccess: 'Batch modification completed: {success} succeeded, {skipped} skipped, {failed} failed',
          failed: 'Batch modification failed: {failed} rates',
          noData: 'No matching unpublished rate records found',
          networkError: 'Batch modification failed. Please try again later.',
          resultSummary: '{total} total, {eligible} eligible, processed in {chunks} chunks',
          failedItems: 'Failed details: {count}'
        },

        // Batch time change
        timeChange: {
          title: 'Batch Time Change',
          operationReminder: 'Operation Reminder',
          reminderContent:
            'This batch modification only applies to the date information of the selected rates. Please proceed with caution.',
          etdTab: 'ETD',
          etaTab: 'ETA',
          validDateTab: 'Valid Period',
          setNewEtd: 'Set new ETD',
          setNewEta: 'Set new ETA',
          setNewValidDate: 'Set new valid period',
          newEtd: 'New ETD:',
          newEta: 'New ETA:',
          validPeriod: 'Valid Period:',
          selectEtd: 'Select ETD',
          selectEta: 'Select ETA',
          selectEtaPlaceholder: 'Select ETA',
          selectValidDateRange: 'Select valid period',
          invalidDateRange: 'Start date cannot be greater than end date',
          startDate: 'Start Date',
          endDate: 'End Date',
          currentEtd: 'Current ETD',
          currentEta: 'Current ETA',
          currentValidPeriod: 'Current Valid Period',
          selectedSummary: 'Total {count} rates selected. Batch modifying {type}.',
          selectedRatesList: 'Rates to be modified',
          etdUpdateSuccess: 'ETD updated for {count} rates',
          etaUpdateSuccess: 'ETA updated for {count} rates',
          validDateUpdateSuccess: 'Valid period updated for {count} rates',
          updateFailed: 'Batch time change failed',
          tipText: 'Note: The modified date will take effect immediately. Please confirm accuracy.',
          validDate: 'Valid Period'
        }
      },

      // Charge Items
      chargeItems: {
        oceanFreight: 'Ocean Freight',
        operationFee: 'Handling Fee',
        documentationFee: 'Documentation Fee',
        portCharges: 'Port Charges',
        storageFee: 'Storage Fee',
        inlandTransport: 'Inland Transport',
        customsFee: 'Customs Fee',
        insuranceFee: 'Insurance Fee',
        baf: 'BAF',
        caf: 'CAF',
        confirm: {
          onShelf: 'Confirm Online',
          offShelf: 'Confirm Offline',
          delete: 'Confirm Delete',
          onShelfContent: 'Are you sure to online the selected rates?',
          offShelfContent: 'Are you sure to offline the selected rates?',
          deleteContent: 'Are you sure to delete the selected rates?'
        },
        messages: {
          loadDataFailed: 'Data load failed',
          loadDetailFailed: 'Load rate details failed',
          onShelfSuccess: 'Online successful'
        },
        detentionFee: 'Detention Fee',
        demurrageFee: 'Demurrage Fee',
        additionalFee: 'Additional Fee',
        fuelFee: 'Fuel Fee',
        bookingFee: 'Booking Fee',
        inspectionFee: 'Inspection Fee',
        trailerFee: 'Trailer Fee'
      },

      // AI Recognition
      aiRecognition: {
        title: 'AI Smart Recognition',
        uploadTitle: 'Upload Contract File',
        uploadDesc:
          'Click or drag file here, system will automatically recognize content and extract rate info',
        processing: 'AI Recognizing...',
        processingDesc: 'Please wait, AI is analyzing your rate file...',
        success: 'Recognized successfully!',
        successDesc: 'Rate info successfully recognized and imported',
        error: 'Recognition failed',
        errorDesc: 'File recognition failed, please check format or re-upload',
        retry: 'Re-upload',
        aiDescription: 'AI Recognition Info',
        aiFeatures: [
          'AI will automatically recognize ports, carriers, prices etc.',
          'Supports multiple formats and complex table structures',
          'Background task will be created after identification',
          'Please ensure uploaded files are clear and readable'
        ],
        successTitle: 'AI task submitted',
        successDescription:
          'Task is running in background. Please check progress in <strong>System Settings - Task Management</strong>.',
        goToTaskManagement: 'Go to Tasks',
        close: 'Close',
        uploadFile: 'Upload {type} file',
        clickOrDrag: 'Click or drag file here to upload',
        supportedFormats: 'Formats: PDF, Excel, Word, Image etc.',
        selectFile: 'Select File',
        aiParsing: 'AI Parsing...',
        aiParsingTip: 'AI is parsing document, please wait...',
        // Contract Parsing (VAS) redirect
        contractParsingNotActivated: 'Contract Parsing Not Activated',
        contractParsingRequired: 'Please contact sales at 13524664368 (same on WeChat).',
        contractParsingUrlMissing: 'Contract parsing page URL is not configured',
        contractParsingCheckFailed: 'Failed to check contract parsing activation status. Please try again later.'
      },

      // Detail Page
      detail: {
        pageTitles: {
          create: 'New FCL Rate',
          edit: 'Edit FCL Rate',
          view: 'Rate Details',
          copy: 'Copy FCL Rate'
        },

        placeholder: 'Please select',
        buttons: {
          back: 'Back',
          cancel: 'Cancel',
          saveDraft: 'Save Draft',
          save: 'Save',
          saveAndQuote: 'Save & Quote',
          directOnShelf: 'Online Directly',
          aiRecognition: 'AI Recognition',
          edit: 'Edit',
          copy: 'Copy',
          addContainerType: '+ Add Container Type'
        },
        hints: {
          salePriceAuto: 'Auto calculate, overridable',
          freightPriceMin: 'At least one container cost price required'
        },
        copyMode: {
          alert: 'Copying rate info. Rate No. will be regenerated. Please confirm and save.'
        },
        status: {
          label: 'Rate Status:',
          draft: 'Draft',
          normal: 'Normal',
          expired: 'Expired',
          offline: 'Offline',
          offShelf: 'Offline',
          unknown: 'Unknown'
        },
        sections: {
          basicInfo: 'Basic Information',
          routeInfo: 'Route Information',
          carrierInfo: 'Carrier Information',
          validitySettings: 'Validity Settings',
          remarks: 'Remarks',
          rateDetails: 'Rate Details',
          rateInfo: 'Rate Information',
          oceanFreight: 'Ocean Freight',
          oceanFreightSurcharge: 'Ocean Freight Surcharge',
          polSurcharge: 'Origin Port Charges',
          podSurcharge: 'POD Charges',
          increaseRule: 'Markup Rule',
          surchargeDetails: 'Surcharge Details'
        },

        // badges
        badges: {
          matched: 'Matched'
        },
        fields: {
          priceNo: 'Rate No.',
          batchNo: 'Batch No.',
          rateType: 'Rate Type',
          por: 'Place of Receipt',
          pol: 'POL',
          polTerminal: 'POL Terminal',
          podTerminal: 'POD Terminal',
          pdl: 'PDL',
          pod: 'POD',
          podRouteCategory: 'Destination Route',
          laneId: 'Route',
          laneCode: 'Route Code',
          shipName: 'Vessel',
          voyageNumber: 'Voyage',
          departureWeekdays: 'Departure Weekdays',
          cutoffWeekdays: 'Cutoff Weekdays',
          unboxDelivery: 'Unbox Delivery',
          weightLimit: 'Weight Limit',
          freightCurrency: 'Currency',
          freightPaymentType: 'Payment Terms',
          onePrice: 'One Price',
          containerType: 'Container Type',
          costPrice: 'Cost Price',
          salePrice: 'Sale Price',
          etd: 'ETD',
          eta: 'ETA',
          vesselSchedule: 'Vessel Schedule',
          voyage: 'Transit Time',
          pot1: 'Transit Port (1st)',
          pot2: 'Transit Port (2nd)',
          pot3: 'Transit Port (3rd)',
          carrier: 'Carrier',
          contractNo: 'Contract No.',
          nac: 'NAC',
          freeContainerDays: 'Free Container',
          freeStorageDays: 'Free Demurrage',
          validPeriod: 'Valid Period',
          overweightNote: 'Overweight Note',
          chargeSpecialNote: 'Special Note',
          notes: 'Remarks',
          ruleName: 'Rule Name',
          rulesId: 'Rule ID',
          increasePriceType: 'Markup Type',
          increasePercentage: 'Markup Percentage',
          markupAmount: 'Markup Amount',
          branch: 'Branch',
          cargoDescription: 'Cargo Description',
          tradeTerms: 'Trade Terms',
          detentionFreeDays: 'Detention Free Days',
          demurrageFreeDays: 'Demurrage Free Days'
        },
        placeholders: {
          selectRateType: 'Select rate type',
          selectPlaceOfDelivery: 'Select place of receipt',
          selectPol: 'Select POL',
          selectPolTerminal: 'Select POL Terminal',
          selectPodTerminal: 'Select POD Terminal',
          selectPortOfDischarge: 'Select POD',
          selectPod: 'Select POD',
          selectPdl: 'Select PDL',
          selectPodRouteCategory: 'Select destination route',
          selectLaneId: 'Select route',
          inputLaneCode: 'Enter route code',
          inputShipName: 'Enter vessel name',
          inputVoyageNumber: 'Enter voyage',
          selectDepartureWeekdays: 'Select departure weekdays',
          selectCutoffWeekdays: 'Select cutoff weekdays',
          selectUnboxDelivery: 'Select unbox delivery',
          inputWeightLimit: 'Enter weight limit',
          selectFreightCurrency: 'Select currency',
          selectFreightPaymentType: 'Select payment terms',
          selectOnePrice: 'Select one-price option',
          inputCostPrice: 'Enter cost price',
          autoCalculate: 'Auto calculate',
          inputVoyage: 'Enter transit time',
          inputSpecialNote: 'Enter special note',
          selectEtd: 'Select ETD',
          selectEta: 'Select ETA',
          selectVesselSchedule: 'Select vessel schedule',
          copyPriceNo: 'Regenerated after copying, e.g. FCL2506250001',
          autoGeneratePriceNo: 'Auto generated, e.g. FCL2506250001',
          selectPot1: 'Select transit port (1st)',
          selectPot2: 'Select transit port (2nd)',
          selectPot3: 'Select transit port (3rd)',
          selectCarrier: 'Select carrier',
          selectContractNo: 'Select contract number',
          selectNac: 'Select NAC',
          inputFreeContainerDays: 'Enter free container days',
          inputFreeStorageDays: 'Enter free demurrage days',
          startDate: 'Start Date',
          endDate: 'End Date',
          inputOverweightNote: 'Enter overweight note',
          inputChargeSpecialNote: 'Enter special note',
          inputNotes: 'Enter remarks',
          selectFeeName: 'Select fee name',
          autoFill: 'Auto fill',
          inputContainerPrice: 'Enter price',
          selectBillingUnit: 'Select unit',
          selectBranch: 'Select branch',
          inputCargoDescription: 'Enter cargo description',
          selectTradeTerms: 'Select trade terms',
          inputDetentionFreeDays: 'Enter detention free days',
          inputDemurrageFreeDays: 'Enter demurrage free days'
        },
        validation: {
          selectPot1: 'Please select transit port (1st)',
          selectCarrier: 'Please select carrier',
          selectValidPeriod: 'Please select valid period',
          selectBranch: 'Please select branch',
          selectTradeTerms: 'Please select trade terms',
          selectSupplier: 'Please select supplier',
          selectCargoType: 'Please select cargo type',
          selectSpaceStatus: 'Please select space status',
          selectSpecialPriceStatus: 'Please select special price status',
          selectPriceTrend: 'Please select price trend',
          selectContainerOwnership: 'Please select container ownership',
          selectFreightCurrency: 'Please select freight currency',
          selectFreightPaymentType: 'Please select payment type',
          selectPlaceOfDeliveryOrPol: 'Please fill in at least one: Place of Delivery or POL',
          selectPortOfDischargeOrPod: 'Please fill in at least one: Port of Discharge or FND'
        },
        messages: {
          selectRateType: 'Please select rate type',
          selectPol: 'Please select POL',
          selectPod: 'Please select FND',
          selectLaneId: 'Please select route',
          saveDataFirst: 'Please save data first',
          onlyDraftCanSave: 'Only drafts can be saved',
          onlyDraftCanSaveDraft: 'Only drafts can be saved as draft',
          draftSaveSuccess: 'Draft saved',
          draftSaveFailed: 'Save draft failed',
          rateUpdateSuccess: 'Rate updated!',
          rateCreateSuccess: 'Rate created!',
          rateUpdateAndOnShelfSuccess: 'Rate updated and published!',
          rateCreateAndOnShelfSuccess: 'Rate created and published!',
          saveAndQuoteSuccess: 'Rate saved and business quote created',
          missingInquiryId: 'Missing inquiry ID, cannot save and quote',
          copySuccess: 'Rate info copied. Rate No. will be regenerated. Please confirm and save.',
          saveFailed: 'Save failed',
          checkRequiredFields: 'Please check required fields',
          unknownError: 'Unknown error',
          loadDataFailed: 'Load data failed',
          copyDataLoadFailed: 'Copy data load failed',
          unknownFee: 'Unknown fee',
          getBasicFeeFailed: 'No basic fee found',
          containerSettingsUpdated: 'Container settings updated',
          loadFeeDataFailed: 'Load fee data failed',
          feeTypeRequired: 'Row {row}: Please select fee location type',
          atLeastOneContainerType:
            'Row {row}: At least one container price is required for box-based billing',
          unitRateRequired: 'Row {row}: Unit rate is required for BL-based billing'
        },
        modals: {
          containerSettings: 'Container Settings',
          aiRecognition: 'AI Rate Recognition'
        },
        rateDetails: {
          title: 'Rate Details',
          containerBilling: 'Box-based Billing',
          nonContainerBilling: 'Non-box-based Billing',
          boxTypeSettings: 'Box Settings',
          addItem: 'Add Item',
          basicFee: 'Basic Fee',
          delete: 'Delete',
          noData: 'No Data',
          tableColumns: {
            feeName: 'Fee Name',
            currency: 'Currency',
            billingUnit: 'Unit',
            unitPrice: 'Unit Price',
            specialNote: 'Special Note',
            actions: 'Actions',
            unitRate: 'Fee (BL)',
            paymentType: 'Payment Terms',
            feeType: 'Charge Type',
            rowSource: 'Source',
            surchargeNo: 'Surcharge No.',
            surchargeValidPeriod: 'Valid Period',
            rowSourceManual: 'Manual',
            rowSourceSystem: 'System Rule'
          },
          containerBillingTitle: 'Box-based Items',
          feeNameTitle: 'Fee Name',
          specialNoteTitle: 'Special Note',
          currencyTitle: 'Currency',
          unitTitle: 'Unit',
          priceTitle: 'Price',
          mainline: 'Mainline',
          containerBillingLabel: 'Box-based',
          nonContainerBillingLabel: 'Non-box-based'
        },
        statusLabels: {
          rateStatus: 'Rate Status:'
        },
        buttonTexts: {
          back: 'Back'
        },
        enumLabels: {
          rateTypes: {
            contract: 'Contract',
            spot: 'SPOT E-commerce'
          },
          cargoTypes: {
            general: 'General',
            dangerous: 'Dangerous',
            frozen: 'Frozen',
            special: 'Special',
            steelCoil: 'Steel Coil',
            liquid: 'Liquid',
            chemical: 'Chemical',
            textile: 'Textile'
          },
          spaceStatus: {
            smooth: 'Smooth',
            normal: 'Normal',
            singleApply: 'Single Apply',
            overbooked: 'Overbooked',
            notAccepting: 'Not Accepting',
            limited: 'Limited',
            suspended: 'Suspended',
            inquiry: 'Inquiry'
          },
          priceTrends: {
            rising: 'Rising',
            falling: 'Falling',
            stable: 'Stable'
          },
          transitTypes: {
            direct: 'Direct',
            transit: 'Transit'
          }
        },
        transitPorts: {
          first: 'Transit Port (1st)',
          second: 'Transit Port (2nd)',
          third: 'Transit Port (3rd)',
          selectFirst: 'Select 1st transit port',
          selectSecond: 'Select 2nd transit port',
          selectThird: 'Select 3rd transit port'
        },
        feeNames: {
          documentFee: 'Documentation Fee'
        },
        modalContent: {
          boxTypeSettings: {
            description: 'Select box types to display in table:'
          },
          aiRecognition: {
            uploadTitle: 'Upload Rate File',
            uploadDesc: 'Click or drag file here to upload',
            supportedFormats: 'Formats: PDF, Excel, Word, Image etc.',
            selectFile: 'Select File',
            aiDescription: 'AI Recognition Info',
            aiFeatures: [
              'AI will automatically recognize ports, carriers, prices etc.',
              'Supports multiple formats and complex table structures',
              'Background task will be created after identification',
              'Please ensure uploaded files are clear and readable'
            ]
          }
        },
        notifications: {
          aiRecognitionSuccess: 'AI recognition completed!',
          fileUploadPending: 'File upload under development...'
        },
        confirm: {
          onShelf: 'Confirm Online',
          offShelf: 'Confirm Offline',
          delete: 'Confirm Delete',
          onShelfContent: 'Are you sure to online this rate?',
          offShelfContent: 'Are you sure to offline this rate?',
          deleteContent: 'Are you sure to delete this rate?'
        }
      },
      messages: {
        loadDataFailed: 'Load data failed',
        addSuccess: 'Added successfully',
        editSuccess: 'Edited successfully',
        deleteSuccess: 'Deleted successfully',
        copySuccess: 'Copied successfully',
        onShelfSuccess: 'Online successfully',
        offShelfSuccess: 'Offline successfully',
        batchOnShelfFailed: 'Batch online failed',
        batchOffShelfFailed: 'Batch offline failed',
        batchDeleteFailed: 'Batch delete failed',
        batchPriceFailed: 'Batch price change failed',
        batchTimeFailed: 'Batch time change failed',
        deleteConfirm: 'Are you sure to delete this rate record?',
        exportFilename: 'FCL Rate Data',
        unknown: 'Unknown',
        selectTransitType: 'Select transit type',
        viewDetailFailed: 'View detail failed: missing ID',
        editFailed: 'Edit failed: missing ID',
        copyFailed: 'Copy failed: missing ID',
        loadRateDetailsFailed: 'Load rate details failed',
        selectFeeTypeFirst: 'Please select fee type first',
        inputAtLeastOnePrice: 'Please enter at least one box type price',
        all: 'All',
        onlyDraftCanOnline: 'Only drafts can be online. Current selection includes: {status}',
        onlyNormalCanOffline: 'Only published can be offline. Current selection includes: {status}',
        onlyDraftCanDelete: 'Only drafts can be deleted. Current selection includes: {status}',
        onlyDraftCanModify: 'Only drafts can be modified',
        importTitle: 'Import FCL Rates',
        importDesc: 'Upload Excel file using FCL3.1 template (.xls/.xlsx).',
        importSelectFile: 'Select File',
        importSubmit: 'Import',
        importSelectFileFirst: 'Please select file first',
        importParsing: 'Uploading and verifying...',
        importSuccess:
          'Parsed: {sheetCount} sheets, {totalRows} rows. Saved: {savedSuccess} success, {savedFailed} failed. Empty: {skipped}. Batch: {batchNo}',
        importFailed: 'Import failed',
        templateDownloadFailed: 'Template download failed',
        onlyDraftCanOnlineDetail:
          'Only Draft, Expired or Offline can be online. Selection includes: {status}',
        onlyNormalCanOfflineDetail: 'Only Published can be offline. Selection includes: {status}',
        onlyDraftCanDeleteDetail: 'Only Drafts can be deleted. Selection includes: {status}',
        onlyDraftCanModifyDetail: 'Only Drafts can be modified. Selection includes: {status}',
        disabledStatusOnlineFailed: 'Disabled rate cannot be online',
        statusToggleFailed: 'Status toggle failed',
        statusToggleForbidden: 'Rate with {status} status cannot toggle online/offline',
        loadRateDetailFailed: 'Load rate detail failed',
        selectContainerTypeFirst: 'Please select at least one container type',
        atLeastOneContainerConfig: 'At least one container configuration is required',
        containerTypeAlreadySelected:
          'This container type is already selected, please choose another',
        importReminderTitle: 'Instructions',
        importReminderLine1: '1. Rates will be stored in draft. They take effect after publishing.',
        importReminderLine2: '2. Existing prices for the same period will be overwritten.',
        loadFeeCacheFailed: 'Failed to load fee data',
        feeTypeFallback: 'Fee Type {feeType}',
        toggleStatusFailed: 'Status toggle failed, please try again',
        exportSuccess: 'Export successful',
        exportFailed: 'Export failed'
      },
      ratio: {
        actions: {
          ratio: 'Rate Ratio',
          generate: 'Generate',
          saveDraft: 'Save Draft',
          clearDraft: 'Clear Draft',
          addRow: 'Add Source',
          removeRow: 'Remove',
          downloadUnmatched: 'Download Unmatched Details',
          goToList: 'Go to Rate Maintenance',
          reload: 'Reload'
        },
        modal: {
          title: 'Rate Ratio',
          sourceTitle: 'Sources (2-3, at least 1 FAK + 1 Name Account (Fix))',
          colSort: 'No.',
          colBatchNo: 'Source Batch No',
          colWeight: 'Weight',
          colContractNo: 'Contract No',
          colNature: 'Offer Nature',
          colPodRoute: 'POD Route Category',
          colPdl: 'Destination Port',
          colPriceRange: 'Price Validity',
          effectiveRange: 'Ratio Validity Period',
          weightPlaceholder: '0-9.9',
          batchPlaceholder: 'Enter batch no',
          contractPlaceholder: 'Enter contract no',
          naturePlaceholder: 'Select offer nature',
          podRoutePlaceholder: 'Select route',
          pdlPlaceholder: 'Select destination port',
          effectiveMismatchTitle: 'Validity Mismatch Warning',
          effectiveMismatchContent: 'The original rate validity period ({origBegin} ~ {origEnd}) does not match the entered period ({curBegin} ~ {curEnd}).',
          comboOnShelfWarn: 'This contract combo already has an active ratio price. Continuing will create a draft; take the old batch offline before listing.',
          noOverlapWarn: 'Selected batches have no overlapping validity. Please set the ratio validity period manually.',
          draftLoaded: 'Unfinished draft loaded',
          draftSaved: 'Draft saved',
          draftCleared: 'Draft cleared',
          duplicateBatchWarn: 'Duplicate batch no',
          duplicateContractWarn: 'Duplicate contract no',
          duplicateSourceWarn: 'Duplicate source batch or contract found',
          hasInvalidRowWarn: 'Some sources failed to load. Please fix or remove them',
          minSourcesWarn: 'At least 2 sources are required',
          missingWeightWarn: 'Please enter valid weights (0.1-9.9) for all sources',
          missingNatureWarn: 'Please select offer nature for all sources',
          comboNatureWarn: 'Ratio sources must include at least 1 FAK and 1 Name Account (Fix)',
          carrierMismatchWarn: 'All contracts must belong to the same carrier.',
          missingEffectiveRangeWarn: 'Please select ratio validity period',
          missingNatureTopTip: 'Some contracts have incompatible nature, are unset, or not filed. You can save draft, but must complete/modify them in Contract Management before generating ratio.',
          missingNatureDialogTitle: 'Cannot Generate Ratio: Incompatible Contract Nature',
          promptDialogDesc: 'The following source contracts do not have a valid offer nature for ratio calculation. Rate ratio only supports FAK or Name Account (BCO) contracts. Please update them in [Contract Management]:',
          promptCaseNotFound: 'Contract [{contractNo}]: Does not exist in Contract Management. Please add it.',
          promptCaseNatureMissing: 'Contract [{contractNo}]: Contract exists but offer nature is not set. Please supplement as FAK or BCO.',
          promptCaseNatureIncompatible: 'Contract [{contractNo}]: Current nature is [{nature}]. Rate ratio only supports FAK or Name Account (BCO). Please modify it.',
          promptDraftNote: 'Note: Clicking "Go to Contract Management" below will automatically save your current draft.',
          tagNatureMissing: 'Nature Unset',
          tagNatureIncompatible: 'Incompatible: {nature}',
          tagContractNotFound: 'Contract Not Found',
          tipNatureMissing: 'Contract exists but offer nature is not set. Please supplement before generating ratio.',
          tipNatureIncompatible: 'Current nature is [{nature}]. Rate ratio only supports FAK or Name Account (BCO). Please modify before generating ratio.',
          tipContractNotFound: 'Contract does not exist in Contract Management. Please add before generating ratio.',
          actionSupplement: 'Supplement',
          actionModify: 'Modify',
          actionAddContract: 'Add Contract',
          goToContractManagement: 'Go to Contract Management',
          refreshCheck: 'Refresh / Re-verify',
          tipRefresh: 'Re-verify offer nature',
          natureRefreshedSuccess: 'Contract [{contractNo}] nature updated to: {nature}',
          natureStillMissing: 'Contract [{contractNo}] exists, but offer nature is still missing',
          natureStillIncompatible: 'Contract [{contractNo}] nature is still [{nature}], which does not meet ratio requirements (FAK or BCO required)',
          contractStillNotFound: 'Contract [{contractNo}] was not found in Contract Management'
        },
        progress: {
          title: 'Ratio Task Progress',
          completedTitle: 'Rate Ratio Completed',
          completedSubtitle: 'The calculated rates have been automatically imported into [Rate Management]. Search this batch with the Task ID below.',
          processingTitle: 'Rate Ratio Processing',
          processingSubtitle: 'Calculating rate ratio, please wait...',
          failedTitle: 'Rate Ratio Failed',
          taskIdLabel: 'Rate Ratio Import Task ID:',
          downloadFile: 'Download File',
          goToRateMaintenance: 'Go to Rate Management',
          sourceRatesLabel: 'Source Contract Rates for Ratio',
          generatedRatesLabel: 'Generated Rates Count',
          unmatchedRatesLabel: 'Unmatched Rates Count',
          rowsUnit: 'Rows',
          noUnmatchedFile: 'No unmatched details file available',
          stepRead: 'Read Contract Rates',
          stepMatch: 'Rate Matching & Calculation',
          stepValidate: 'Result Validation',
          stepDone: 'Ratio Completed',
          statMatchedDimension: 'Matched Dimension Rows',
          statMatchedContainer: 'Matched Container Types',
          statUnmatchedDimension: 'Unmatched Dimension Rows',
          statUnmatchedContainer: 'Unmatched Container Types',
          statInserted: 'Rates Generated',
          failReason: 'Failure Reason',
          cleanupHint: 'Cleanup Hint',
          pollFailHint: 'Progress query error, retrying…',
          pollStopped: 'Progress query failed repeatedly. Auto refresh stopped.'
        },
        nature: {
          fak: 'FAK',
          nacFix: 'Name Account (Fix)',
          ratio: 'Ratio Price'
        }
      },
      // [CCTOWER-3605] AI Parse choice modal
      aiParseChoice: {
        title: 'Select AI Parsing Method',
        textTitle: 'Text Rate Parsing',
        textDesc: 'Paste unstructured quote messages from WeChat, emails, or QQ. AI extracts ports, schedules, and container rates to generate a draft.',
        goText: 'Open Assistant →',
        contractTitle: 'Contract File Parsing',
        contractDesc: 'Upload shipping line/carrier booking notes or contracts in Excel/PDF format for deep parsing and validation.',
        goContract: 'Go to File Parsing →',
        checkingTag: 'Checking',
        checkingButton: 'Checking…',
        pendingTag: 'Under Review',
        rejectedTag: 'Rejected',
        disabledTag: 'Disabled',
        notActivatedTag: 'Not Activated',
        unavailableTag: 'Unavailable',
        errorTag: 'Status Fetch Failed',
        contactSales: 'Contact Sales →',
        goPending: 'View Review Status →',
        retryCheck: 'Click to Retry →',
        statusPendingTitle: 'Contract Parsing Under Review',
        statusPendingDesc: 'Your activation request is under review. Please wait for the result.',
        statusRejectedTitle: 'Contract Parsing Request Rejected',
        statusRejectedDesc: 'Please contact sales at 13524664368 (same on WeChat) to reapply.',
        statusDisabledTitle: 'Contract Parsing Disabled',
        statusDisabledDesc: 'Please contact sales at 13524664368 (same on WeChat) to restore access.',
        statusUnavailableTitle: 'Contract Parsing Unavailable',
        statusUnavailableDesc: 'Please contact sales at 13524664368 (same on WeChat) for activation.'
      },
      // [CCTOWER-3605] Text Rate Parsing Preview Page
      textRatePreview: {
        breadcrumb: {
          list: 'FCL Rates',
          preview: 'Text Rate Parsing Preview'
        },
        loading: 'Loading text rate parsing data...',
        notFound: {
          title: 'Rate Batch Data Not Found',
          subtitle: 'Please check if the batchNo parameter in the URL is correct',
          back: 'Back to Chat'
        },
        summary: {
          title: 'Rate Batch Summary',
          typeTag: 'AI Text Parsing',
          statusDraft: 'Pending Confirmation',
          statusActive: 'Published',
          statusCancelled: 'Cancelled',
          carrier: 'Carrier',
          itemCount: 'Rate Count',
          pol: 'POL',
          missingPolTag: 'POL Missing',
          cargoType: 'Cargo Type',
          validity: 'Validity Period',
          aiInferred: 'AI Inferred',
          currency: 'Base Currency',
          trendNote: 'Rate Trend Note',
          missingPolAlertTitle: 'POL Missing Notice',
          missingPolAlertContent: 'AI could not detect the port of loading (POL) from the original text. Please supplement the POL manually in the rate management list after import.',
          remarks: '📝 Remarks:'
        },
        cargoTypes: {
          general: 'General Cargo',
          dangerous_goods: 'Dangerous Goods (DG)',
          reefer: 'Reefer',
          special: 'Special Container'
        },
        table: {
          title: 'Rate Details',
          count: '{count} rate(s) in total',
          index: 'No.',
          por: 'POR',
          pol: 'POL',
          pod: 'POD',
          pdl: 'PDL',
          laneCode: 'Lane Code',
          vesselVoyage: 'Vessel / Voyage',
          etd: 'ETD',
          validity: 'Validity',
          podCode: 'Port Code',
          gp20: '20GP',
          gp40: '40GP',
          hc20: '20HC',
          hc40: '40HC / HQ',
          hc45: '45HC / HQ',
          rf20: '20RF',
          rf40: '40RF',
          nor20: '20NOR',
          nor40: '40NOR',
          tk20: '20TK',
          tk40: '40TK',
          ot20: '20OT',
          ot40: '40OT',
          fr20: '20FR',
          fr40: '40FR',
          expandContainers: 'Expand Containers',
          collapseContainers: 'Collapse Containers',
          surcharge: 'Surcharge',
          perContainer: '/CTR',
          perBill: '/BL',
          confidence: 'Confidence',
          confidenceHigh: '🟢 High',
          confidenceMedium: '🟡 Medium',
          confidenceLow: '🔴 Low',
          note: 'Note'
        },
        footer: {
          statusLabel: 'Current Status:',
          statusDraftDesc: 'Draft Pending Confirmation (not visible to customers)',
          statusActiveDesc: 'Published (available in rate system)',
          statusCancelledDesc: 'Cancelled / Discarded',
          backBtn: 'Back to Chat',
          cancelBtn: 'Discard Import',
          confirmBtn: '✅ Confirm & Publish',
          downloadExcelBtn: '📥 Export Rates Excel',
          viewListBtn: 'View in Rate Management List'
        },
        messages: {
          missingBatchNo: 'URL is missing batchNo parameter, cannot locate rate draft',
          loadFailed: 'Failed to retrieve text rate draft for this batch',
          networkError: 'Network or server error, please try again later',
          confirmTitle: 'Confirm & Publish Rates',
          confirmContent: 'Are you sure you want to officially import and publish the {count} rate(s) in batch {batchNo}?',
          confirmOk: 'Confirm Import',
          confirmCancel: 'Review Again',
          confirmSuccess: 'Rates successfully imported and published!',
          confirmSuccessDownloadHint: 'Rates have been imported and published! You can view them in the rate management list or go back to chat.',
          confirmFailed: 'Failed to confirm rates',
          cancelTitle: 'Discard This Import',
          cancelContent: 'Are you sure you want to discard batch {batchNo}? The draft will be abandoned.',
          cancelOk: 'Confirm Discard',
          cancelCancel: 'Cancel',
          cancelSuccess: 'Rate draft has been discarded',
          cancelFailed: 'Failed to cancel',
          exportSuccess: 'Rate Excel exported successfully',
          exportFailed: 'Failed to export rate Excel'
        }
      }
    },

    // Pre-port Rate Management
    preport: {
      title: 'Pre-port Rate Management',
      description: 'Manage pre-port rate information',
      pageTitles: {
        create: 'New Pre-port Rate',
        edit: 'Edit Pre-port Rate',
        detail: 'Pre-port Rate Detail',
        copy: 'Copy Pre-port Rate'
      },
      filters: {
        priceNo: 'Rate No.',
        priceNoPlaceholder: 'Enter rate number',
        priceMethod: 'Rate Type',
        priceMethodPlaceholder: 'Select rate type',
        transportMode: 'Transport Mode',
        transportModePlaceholder: 'Select transport mode',
        pol: 'POL',
        polPlaceholder: 'Select POL',
        pod: 'POD',
        podPlaceholder: 'Select POD',
        status: 'Status',
        statusPlaceholder: 'Select status',
        beginDate: 'Effective Date',
        beginDatePlaceholder: 'Select start date',
        endDate: 'Expiry Date',
        endDatePlaceholder: 'Select end date',
        branchLine: 'Branch & Sea-Rail Type',
        branchLinePlaceholder: 'Select branch & sea-rail type',
        portType: 'Port Type',
        portTypePlaceholder: 'Select port type',
        wharf: 'Wharf',
        wharfPlaceholder: 'Enter wharf name',
        supplier: 'Supplier',
        supplierPlaceholder: 'Select supplier',
        remarks: 'Remarks',
        remarksPlaceholder: 'Enter remarks'
      },
      priceMethods: {
        direct: 'Direct',
        branch: 'Branch',
        seaRail: 'Sea-Rail'
      },
      transportModes: {
        truck: 'Truck',
        barge: 'Barge',
        rail: 'Rail'
      },
      branchLines: {
        haiding: 'Haiding Branch',
        zhapu: 'Zhapu Branch'
      },
      seaRailTypes: {
        huzhou: 'Huzhou Sea-Rail',
        yiwu: 'Yiwu Sea-Rail'
      },
      portTypes: {
        sea: 'Sea Port',
        air: 'Air Port'
      },
      status: {
        draft: 'Draft',
        published: 'Published',
        invalid: 'Invalid',
        offline: 'Offline',
        disabled: 'Disabled',
        pendingImport: 'Pending Import'
      },
      cargoTypes: {
        general: 'General',
        dangerous: 'Dangerous',
        refrigerated: 'Refrigerated',
        special: 'Special',
        steel: 'Steel',
        liquid: 'Liquid',
        chemical: 'Chemical',
        textile: 'Textile'
      },
      serviceTypes: {
        FCL: 'FCL',
        LCL: 'LCL',
        TRUCK: 'Truck'
      },
      columns: {
        priceNo: 'Rate No.',
        priceMethod: 'Rate Type',
        transportMode: 'Transport Mode',
        branchLine: 'Branch Type',
        pol: 'POL',
        wharf: 'Wharf',
        regionItems: 'Origin Region',
        gp20: '20GP',
        gp40: '40GP',
        hc20: '20HC',
        hc40: '40HC',
        hc45: '45HC',
        nor20: '20NOR',
        nor40: '40NOR',
        tk20: '20TK',
        tk40: '40TK',
        ot20: '20OT',
        ot40: '40OT',
        fr20: '20FR',
        fr40: '40FR',
        costPrice: 'Cost',
        sellPrice: 'Selling Price',
        supplier: 'Supplier',
        validPeriod: 'Valid Period',
        beginDate: 'Effective Date',
        endDate: 'Expiry Date',
        status: 'Status',
        remarks: 'Remarks',
        createdBy: 'Created By',
        modifiedBy: 'Modified By',
        dateCreated: 'Created Time',
        dateModified: 'Modified Time',
        actions: 'Actions'
      },
      actions: {
        search: 'Search',
        reset: 'Reset',
        add: 'New Pre-port Rate',
        import: 'Import',
        downloadTemplate: 'Download Template',
        view: 'Details',
        edit: 'Edit',
        copy: 'Copy',
        more: 'More',
        onShelf: 'Online',
        offShelf: 'Offline',
        delete: 'Delete',
        batchOperations: 'Batch Operations',
        batchOnShelf: 'Batch Online',
        batchOffShelf: 'Batch Offline',
        batchDelete: 'Batch Delete',
        batchPriceChange: 'Batch Price Change',
        aiRecognition: 'AI Recognition',
        batchValidityChange: 'Batch Time Change'
      },
      aiRecognition: {
        title: 'Pre-port AI Recognition',
        uploadTitle: 'Upload Document',
        uploadDesc: 'Supports PDF, Word, Excel. AI will automatically recognize rate info.',
        aiDescription: 'AI Recognition Info',
        aiFeatures: [
          'AI will automatically recognize ports, carriers, prices etc.',
          'Supports multiple formats and complex table structures',
          'Background task will be created after identification',
          'Please ensure uploaded files are clear and readable'
        ],
        successTitle: 'AI task submitted',
        successDescription:
          'Task is running in background. Please check progress in <strong>System Settings - Task Management</strong>.',
        goToTaskManagement: 'Go to Tasks',
        close: 'Close',
        uploadFile: 'Upload {type} file',
        clickOrDrag: 'Click or drag file here to upload',
        supportedFormats: 'Formats: PDF, Excel, Word, Image etc.',
        selectFile: 'Select File',
        aiParsing: 'AI Parsing...',
        aiParsingTip: 'AI is parsing document, please wait...'
      },
      import: {
        title: 'Pre-port Rate Import',
        downloadTemplate: 'Download Pre-port Rate Template',
        breadcrumb: 'Pre-port Import',
        reminderTitle: 'Instructions',
        reminderLines: [
          '1. Rates will be stored in Draft. Online to take effect.',
          '2. Existing prices for the same period will be overwritten.'
        ],
        dragText: 'Click or drag Excel file here',
        parsing: 'Parsing...',
        cancel: 'Cancel',
        submit: 'Submit',
        fallbackFilename: 'Preport_Rate_Template.xlsx'
      },
      batch: {
        selected: 'Selected {count} items',
        onShelfConfirm: 'Are you sure to online {count} rates?',
        offShelfConfirm: 'Are you sure to offline {count} rates?',
        deleteConfirm: 'Are you sure to delete {count} rates? Cannot be undone.',
        onShelfSuccess: 'Online {count} rates successfully',
        offShelfSuccess: 'Offline {count} rates successfully',
        deleteSuccess: 'Delete {count} rates successfully',
        priceChange: {
          title: 'Batch Price Change',
          selectedRates: 'Selected {count} rates',
          operationReminder: 'Operation Reminder',
          reminderContent:
            "This operation will modify fee amounts for all selected rates. Please proceed with caution. Select specific fee type to modify. Blank means no change. If original rate doesn't have the box type, adjustment won't take effect. Enter positive integers only.",
          selectCharge: 'Select Fee',
          selectChargeDesc: 'Select fee type, e.g. Pre-port Fee, Handling Fee, etc.',
          selectChargePlaceholder: 'Select fee type',
          selectPriceMode: 'Select mode',
          priceIncrease: 'Increase',
          priceDecrease: 'Decrease',
          setPriceMode: 'Fixed Amount',
          priceIncreaseDesc: 'Select increase and enter amount',
          priceDecreaseDesc: 'Select decrease and enter amount',
          setPriceDesc: 'Select fixed and enter amount',
          setPrice: 'Set Price',
          selectChargeFirst: 'Select fee type then set price',
          rateListToModify: 'Rates to be modified',
          rateDetailsList: 'Rates list',
          rateNo: 'Rate No.',
          origin: 'Origin',
          pol: 'POL',
          supplier: 'Supplier',
          status: 'Status',
          showChargePrice: '(Showing {charge} price)',
          showAllCharges: '(Showing total price of box-based fees)',
          noData: 'No Data',
          selectedSummary: 'Total {count} rates selected. Modifying {type}.',
          currentChargeType: 'Current fee type:',
          confirmOperation: 'This will modify the',
          confirmOperationSuffix: 'amount for selected rates.',
          selectChargeAndPrice: 'Set fee type and price first.',
          confirm: 'Confirm',
          cancel: 'Cancel',
          batchPriceSuccess: 'Batch price change successful',
          priceInputRequired: 'Enter at least one box type price',
          pricePlaceholders: {
            increase: 'Increase amount',
            decrease: 'Decrease amount',
            setPrice: 'Fixed price',
            default: 'Enter price'
          }
        },
        timeChange: {
          title: 'Batch Time Change',
          operationAlert: 'Proceed with caution when modifying multiple rates.',
          confirmOperation:
            'This will modify time info for all selected rates. Please confirm data accuracy.',
          etdTab: 'ETD',
          etaTab: 'ETA',
          validDateTab: 'Valid Period',
          setNewEtd: 'Set new ETD',
          setNewEta: 'Set new ETA',
          setNewValidDate: 'Set new valid period',
          newEtd: 'New ETD:',
          newEta: 'New ETA:',
          validPeriod: 'Valid Period:',
          selectEtd: 'Select ETD',
          selectEta: 'Select ETA',
          startDate: 'Start Date',
          endDate: 'End Date',
          currentEtd: 'Current ETD',
          currentEta: 'Current ETA',
          currentValidPeriod: 'Current Valid Period',
          rateListToModify: 'Rates list',
          selectedSummary: 'Selected {count} rates. Modifying {type}.',
          cancel: 'Cancel',
          confirm: 'Confirm',
          etd: 'ETD',
          eta: 'ETA',
          validDate: 'Validity',
          operationReminder: 'Operation Reminder',
          reminderContent: 'This will modify time info for all selected rates.',
          setNewEtdTitle: 'Set New ETD',
          setNewEtaTitle: 'Set New ETA',
          setNewValidDateTitle: 'Set New Validity',
          etdLabel: 'ETD',
          etaLabel: 'ETA',
          validDateLabel: 'Validity',
          selectEtdPlaceholder: 'Select ETD',
          selectEtaPlaceholder: 'Select ETA',
          selectValidDateRange: 'Select validity range',
          invalidDateRange: 'Start date must be before end date',
          startDatePlaceholder: 'Start Date',
          endDatePlaceholder: 'End Date',
          selectedRatesList: 'Rates list',
          tipText: 'Confirm data accuracy before proceeding.',
          etdUpdateSuccess: 'ETD updated for {count} rates',
          etaUpdateSuccess: 'ETA updated for {count} rates',
          validDateUpdateSuccess: 'Validity updated for {count} rates',
          updateFailed: 'Batch time change failed'
        },
        validityChange: {
          title: 'Batch Validity Change',
          validityPeriod: 'Validity',
          selectValidityPeriod: 'Select validity',
          startDate: 'Start Date',
          endDate: 'End Date'
        },
        confirm: {
          online: 'Confirm Online',
          offline: 'Confirm Offline',
          delete: 'Confirm Delete',
          onlineContent: 'Online {count} rates?',
          offlineContent: 'Offline {count} rates?',
          deleteContent: 'Delete {count} rates? Cannot be undone.'
        },
        messages: {
          onlineSuccess: 'Successfully published {count} rates',
          onlineFailed: 'Batch online failed',
          offlineSuccess: 'Successfully offlined {count} rates',
          offlineFailed: 'Batch offline failed',
          deleteSuccess: 'Successfully deleted {count} rates',
          deleteFailed: 'Batch delete failed',
          validityChangeSuccess: 'Batch validity successful',
          validityChangeFailed: 'Batch validity failed',
          selectRatesFirst: 'Select rates to {action} first',
          selectRatesToOnline: 'Select rates to online first',
          selectRatesToOffline: 'Select rates to offline first',
          selectRatesToModify: 'Select rates to modify first',
          selectRatesToDelete: 'Select rates to delete first',
          onlyDraftCanOnline: 'Only Draft can be online. Selection includes: {status}',
          onlyNormalCanOffline: 'Only Published can be offline. Selection includes: {status}',
          onlyDraftCanModify: 'Only Draft can be modified',
          onlyDraftCanDelete: 'Only Draft can be deleted'
        }
      },
      chargeItems: {
        preportFee: 'Pre-port Fee',
        handlingFee: 'Handling Fee',
        documentFee: 'Documentation Fee',
        truckingFee: 'Trucking Fee'
      },
      batchPrice: {
        title: 'Batch Price Change',
        chargeItemRequired: 'Select fee type',
        priceInputRequired: 'Enter at least one box type price',
        priceUpdateFailed: 'Batch price change failed',
        setPriceSuccess: 'Fixed price set for {count} rates',
        increaseSuccess: 'Increased {count} rates',
        decreaseSuccess: 'Decreased {count} rates',
        pricePlaceholders: {
          increase: 'Increase amount',
          decrease: 'Decrease amount',
          setPrice: 'Fixed price',
          default: 'Enter price'
        }
      },
      messages: {
        loadDataFailed: 'Load data failed',
        loadDataError: 'Load pre-port data failed:',
        copySuccess: 'Rate info copied. Rate No. will be regenerated. Confirm and save.',
        batchOnShelfFailed: 'Batch online failed',
        batchOffShelfFailed: 'Batch offline failed',
        batchDeleteFailed: 'Batch delete failed',
        importPending: 'Importing under development',
        templateDownloadFailed: 'Template download failed',
        onShelfSuccess: 'Rate is online',
        offShelfSuccess: 'Rate is offline',
        statusToggleFailed: 'Status toggle failed',
        deleteConfirm: 'Are you sure to delete this rate?',
        deleteSuccess: 'Delete successful',
        deleteFailed: 'Delete failed',
        totalCount: 'Total {count} items',
        unknown: 'Unknown',
        exportFilename: 'Pre-port Rate Data',
        uploadSuccess: 'File uploaded, parsing...',
        uploadFailed: 'Upload failed: {reason}',
        uploadUnknownStatus: 'Status unknown, check task center',
        uploadError: 'Task trigger failed',
        uploadRedirectTitle: 'Import Note',
        uploadRedirectContent: 'Task triggered. Redirecting to check progress...',
        onlyDraftCanOnline: 'Only Draft, Expired or Offline can be online. Selection: {status}',
        onlyNormalCanOffline: 'Only Published can be offline. Selection: {status}',
        onlyDraftCanModify: 'Only Drafts can be modified. Selection: {status}',
        onlyDraftCanDelete: 'Only Drafts can be deleted. Selection: {status}',
        statusDisabledActionError: 'Disabled rate cannot be online',
        statusActionError: '{status} status cannot toggle online/offline',
        containerRate: 'Box-based',
        nonContainerRate: 'Non-box-based',
        callForQuote: 'Inquiry'
      },
      detail: {
        pageActions: {
          back: 'Back',
          saveDraft: 'Save Draft',
          saveAndPublish: 'Online Directly',
          save: 'Save',
          edit: 'Edit',
          copy: 'Copy',
          copyAlert: 'Rate info copied. No. will be regenerated. Confirm and save.'
        },
        basicInfo: {
          title: 'Basic Information',
          priceNo: 'Rate No.',
          priceNoPlaceholder: 'Auto generated, e.g. PREYYMMDD0001',
          priceNoCopyPlaceholder: 'Regenerated after copying, e.g. PREYYMMDD0001',
          priceMethod: 'Rate Type',
          priceMethodRequired: 'Select rate type',
          branchLine: 'Branch Type',
          branchLinePlaceholder: 'Select branch type',
          branchLineRequired: 'Select branch type',
          seaRailType: 'Sea-Rail Type',
          seaRailTypePlaceholder: 'Select sea-rail type',
          seaRailTypeRequired: 'Select sea-rail type',
          etd: 'ETD',
          etdPlaceholder: 'Select ETD',
          eta: 'ETA',
          etaPlaceholder: 'Select ETA',
          remarks: 'Remarks',
          remarksPlaceholder: 'Enter remarks',
          serviceType: 'Service Type',
          serviceTypePlaceholder: 'Select service type',
          onePrice: 'One Price',
          onePricePlaceholder: 'Select one price',
          receivingInstructions: 'Receiving Instructions',
          receivingInstructionsPlaceholder: 'Enter receiving instructions'
        },
        // Detail form (actual current page texts)
        form: {
          cardTitles: {
            basicInfo: 'Basic Info',
            loadingInfo: 'Loading Location Info',
            originInfo: 'Origin Location Info',
            feeInfo: 'Fee Details',
            dangerousGoods: 'Dangerous Goods Info',
            bargeInfo: 'Barge Info',
            railInfo: 'Railway Info',
            doorPoint: 'Origin Door Point',
            freight: 'Freight',
            surchargeDetail: 'Surcharge Details'
          },
          labels: {
            priceNo: 'Rate No.',
            office: 'Company',
            supplier: 'Supplier',
            cargoType: 'Cargo Type',
            validPeriod: 'Validity',
            unNo: 'UN No.',
            hazardClass: 'Class',
            cargoDescription: 'Cargo Description',
            hsCode: 'HSCODE',
            weightLimit: 'Weight Limit (KG)',
            freeBoxDays: 'Free Box Days',
            voyageDays: 'Voyage (Days)',
            containerOwnership: 'Container Ownership',
            specialPriceStatus: 'Special Price Status',
            transportMode: 'Transport Mode',
            bargeCarrier: 'Barge Carrier',
            loadingPort: 'Loading Port',
            bargeName: 'Barge Name',
            bargeVoyage: 'Barge Voyage',
            bargeEtd: 'ETD',
            bargeEta: 'ETA',
            bargeVoyageDays: 'Barge Voyage (Days)',
            railCompany: 'Railway Company',
            loadingStation: 'Loading Station',
            railLineType: 'Line Category',
            railTrainNo: 'Train No.',
            railDepartureTime: 'Departure Time',
            addressType: 'Address Type',
            pol: 'POL',
            wharf: 'Wharf',
            departureStation: 'Departure Station',
            airport: 'Origin Airport',
            airportTerminal: 'Airport Terminal',
            country: 'Country',
            province: 'Province/State',
            city: 'City',
            district: 'District/County',
            street: 'Township/Street',
            community: 'Community',
            detailAddress: 'Detailed Address',
            postalCode: 'Postal Code',
            feeName: 'Fee Name',
            nameEn: 'English Name',
            feeType: 'Fee Type',
            unit: 'Unit',
            currency: 'Currency',
            price: 'Unit Price',
            minCharge: 'Min Charge',
            maxCharge: 'Max Charge',
            paymentType: 'Payment Terms',
            remark: 'Remark',
            action: 'Action',
            countTotal: 'Count in Total',
            totalIncluded: 'Included in Total',
            feeSource: 'Fee Source',
            doorPoint: 'Door Point'
          },
          placeholders: {
            priceNoCopy: 'Regenerated after copying',
            priceNoAuto: 'Auto generated',
            office: 'Select company',
            supplier: 'Select supplier',
            cargoType: 'Select cargo type',
            startDate: 'Start Date',
            endDate: 'End Date',
            unNo: 'Enter 4 digits',
            hazardClass: 'Select dangerous goods class',
            cargoDescription: 'Enter cargo description',
            hsCode: 'Enter 10-digit HSCODE',
            hsCodeExtra: 'Standard 10-digit code',
            weightLimit: 'Enter weight limit',
            freeBoxDays: 'Enter days',
            voyageDays: 'Enter voyage days',
            containerOwnership: 'Select',
            specialPriceStatus: 'Select',
            bargeCarrier: 'Select barge carrier',
            loadingPort: 'Select loading port',
            bargeName: 'Enter barge name',
            bargeVoyage: 'Enter voyage',
            date: 'Select date',
            autoCalc: 'Auto calculated from ETD/ETA',
            railCompany: 'Select railway company',
            loadingStation: 'Select loading station',
            railLineType: 'Enter line category',
            railTrainNo: 'Enter train no.',
            railDepartureTime: 'Select departure time',
            addressType: 'Select address type',
            pol: 'Select POL',
            wharf: 'Select wharf',
            departureStation: 'Select departure station',
            airport: 'Select origin airport',
            airportTerminal: 'Enter airport terminal (optional)',
            country: 'Select country',
            province: 'Select province/state',
            city: 'Select city',
            district: 'Select district/county',
            street: 'Select township/street',
            community: 'Select community',
            detailAddress: 'Enter detailed address',
            postalCode: 'Enter postal code (optional)',
            regionPlaceholder: 'Select origin door point address',
            unit: 'Unit',
            currency: 'Currency',
            price: 'Unit Price',
            min: 'Min',
            max: 'Max',
            paymentType: 'Select',
            autoEcho: 'Auto display',
            feeName: 'Select',
            select: 'Select'
          },
          actions: {
            add: 'Add',
            addBoxType: 'Add Box Type Column'
          },
          options: {
            truck: 'Truck',
            barge: 'Barge',
            rail: 'Railway',
            seaPort: 'Sea Port',
            railStation: 'Railway Station',
            airport: 'Airport',
            prepaid: 'Prepaid',
            collect: 'Collect',
            notAllowSpecialPrice: 'Not Allow Special Price',
            allowSpecialPrice: 'Allow Special Price',
            specialPrice: 'Special Price',
            container: 'Container',
            systemRule: 'System Rule',
            manual: 'Manual',
            locked: 'Locked',
            preportSurcharge: 'Pre-Port Surcharge',
            freight: 'Freight'
          },
          validation: {
            office: 'Please select company',
            cargoType: 'Please select cargo type',
            validPeriod: 'Please select validity',
            hazardClass: 'Dangerous goods requires class',
            cargoDescription: 'Dangerous goods requires cargo description',
            containerOwnership: 'Please select container ownership',
            specialPriceStatus: 'Please select special price status',
            transportMode: 'Please select transport mode',
            bargeCarrier: 'Please select barge carrier',
            loadingPort: 'Please select loading port',
            railCompany: 'Please select railway company',
            loadingStation: 'Please select loading station',
            addressType: 'Please select address type',
            pol: 'Please select POL',
            departureStation: 'Please select departure station',
            airport: 'Please select origin airport',
            hazardUnNo: 'Dangerous goods requires UN No.',
            unNoFormat: 'UN No. must be 4 digits',
            hazardHsCode: 'Dangerous goods requires HSCODE',
            hsCodeFormat: 'HSCODE must be 10 digits',
            partialSaveSuccess: 'Partial save: {successCount} succeeded, {failedCount} failed',
            fee: {
              freightCurrency: 'Freight row {rowNo}: please select currency',
              freightContainerPrice: 'Freight row {rowNo}: please enter at least one box price',
              freightPrice: 'Freight row {rowNo}: please enter unit price',
              surchargeFeeName: 'Surcharge row {rowNo}: please select fee name',
              surchargeUnit: 'Surcharge row {rowNo}: please select unit',
              surchargeCurrency: 'Surcharge row {rowNo}: please select currency',
              surchargeContainerPrice: 'Surcharge row {rowNo}: please enter at least one box price',
              surchargePrice: 'Surcharge row {rowNo}: please enter unit price'
            }
          },
          hazardClasses: {
            '1': 'Class 1 Explosives',
            '2': 'Class 2 Gases',
            '3': 'Class 3 Flammable Liquids',
            '4': 'Class 4 Flammable Solids',
            '5': 'Class 5 Oxidizers',
            '6': 'Class 6 Toxic Substances',
            '7': 'Class 7 Radioactive Material',
            '8': 'Class 8 Corrosives',
            '9': 'Class 9 Miscellaneous Dangerous Goods'
          }
        },
        regionSelection: {
          title: 'POL Region Selection',
          aiRecognition: 'AI Recognition',
          addRegion: 'Add Region',
          region: 'Region {index}',
          atLeastOneRegion: 'At least one region required',
          administrativeDivision: 'Admin Division',
          detailedLocation: 'Detailed Location',
          detailedAddress: 'Detailed Address',
          selectProvince: 'Select Province',
          selectCity: 'Select City',
          selectDistrict: 'Select District',
          selectStreet: 'Select Street',
          selectCommunity: 'Select Community',
          enterDetailedAddress: 'Enter detailed address',
          noRegionSet: 'No region set',
          clickToExpand: 'Click to expand region settings',
          selectRegionInfo: 'Select region info'
        },
        portInfo: {
          title: 'POL Information',
          pol: 'POL',
          polRequired: 'Select POL',
          polPlaceholder: 'Select POL',
          wharf: 'Wharf',
          wharfPlaceholder: 'Enter wharf',
          supplier: 'Supplier',
          supplierRequired: 'Enter supplier',
          supplierPlaceholder: 'Enter supplier',
          validPeriod: 'Valid Period',
          validPeriodRequired: 'Select validity',
          validPeriodStart: 'Start Date',
          validPeriodEnd: 'End Date',
          etd: 'ETD',
          etdPlaceholder: 'Select ETD',
          eta: 'ETA',
          etaPlaceholder: 'Select ETA',
          remarks: 'Remarks',
          remarksPlaceholder: 'Enter remarks'
        },
        boxTypeSettings: {
          title: 'Box Settings',
          selectBoxTypes: 'Select box types to display:',
          updateSuccess: 'Box settings updated'
        },
        rateDetails: {
          title: 'Rate Details',
          containerRateItems: 'Box-based Items',
          nonContainerRateItems: 'Non-box-based Items',
          addRateItem: 'Add Item',
          rateItem: 'Item {index}',
          atLeastOneRateItem: 'At least one item required',
          addChargingItem: 'Add Charge',
          chargingItem: 'Charge {index}',
          atLeastOneChargingItem: 'At least one charge required',
          specialNote: 'Special Note',
          specialNotePlaceholder: 'Note',
          feeName: 'Fee Name',
          feeNamePlaceholder: 'Select fee name',
          basicFeePlaceholder: 'Select basic fee',
          currency: 'Currency',
          unit: 'Unit',
          unitPlaceholder: 'Select unit',
          price: 'Price',
          pricePlaceholder: 'Enter price',
          priceInputPlaceholder: 'Price',
          autoFill: 'Auto fill',
          basicFee: 'Basic Fee',
          piece: 'Shipment',
          container: 'Container',
          item: 'Piece',
          ton: 'Ton',
          cubicMeter: 'CBM',
          teu: 'TEU',
          actions: 'Actions',
          containerBillingTitle: 'Box-based Items',
          feeNameTitle: 'Fee Name',
          specialNoteTitle: 'Special Note'
        },
        saveMessages: {
          updateSuccess: 'Rate updated and published!',
          createSuccess: 'Rate created and published!',
          saveFailed: 'Save failed: ',
          unknownError: 'Unknown error',
          checkRequired: 'Check required fields',
          draftSaveSuccess: 'Draft saved',
          draftSaveFailed: 'Save draft failed: ',
          onlyDraftCanSave: 'Only drafts can be saved',
          onlyDraftCanSaveDraft: 'Only drafts can be saved as draft'
        },
        dataLoading: {
          loadFailed: 'Load failed: ',
          copyLoadFailed: 'Copy load failed: '
        },
        // Region translations
        region: {
          china: 'China',
          usa: 'USA',
          uk: 'UK',
          germany: 'Germany',
          france: 'France',
          japan: 'Japan',
          korea: 'Korea',
          singapore: 'Singapore',
          australia: 'Australia',
          canada: 'Canada'
        },
        // [CCTOWER-2468] trunk match / leave / transport switch / surcharge footer
        trunkMatch: {
          title: 'Match Trunk Rate',
          staleAlert: 'Match criteria changed. Please refresh.',
          refresh: 'Refresh Match',
          manual: 'Manual Match',
          selectTitle: 'Select Trunk Rate',
          confirm: 'OK',
          cancel: 'Cancel',
          empty: 'No trunk rate matched yet',
          notFound: 'No matching trunk rate found',
          multiCandidates: 'Found {count} candidates. Please select one.',
          matchedSuccess: 'Trunk rate matched',
          selectedSuccess: 'Trunk rate selected',
          needPortType: 'Please select address type first',
          needPol: 'Please select POL first',
          needAirport: 'Please select airport first',
          needRailStation: 'Please select departure station first',
          needSelectCandidate: 'Please select a trunk rate',
          typeLabel: 'Type: ',
          priceNoLabel: 'No.: ',
          officeLabel: 'Office: ',
          supplierLabel: 'Supplier: ',
          periodLabel: 'Validity: ',
          combinedPriceLabel: 'Combined: ',
          trunkType: 'Trunk Type',
          priceNo: 'Rate No.',
          office: 'Office',
          supplier: 'Supplier',
          validPeriod: 'Validity',
          matchedAt: 'Matched At',
          polNode: 'Origin',
          podNode: 'Destination',
          spaceStatus: 'Space Status',
          priceTrend: 'Price Trend',
          weekdays: 'Schedule',
          detentionFreeDays: 'Free Detention',
          voyageDays: 'Transit Days',
          actions: 'Actions',
          detail: 'Details',
          detailTitle: 'Trunk Rate Details',
          openRateDetail: 'Open Rate Detail',
          railDetailUnavailable: 'This trunk type does not support opening the rate detail page',
          inquiry: 'Inquiry',
          combinedBox: 'Pre {pre} + Trunk {trunk} = {total}',
          gp20Total: '20GP Total',
          gp40Total: '40GP Total',
          hc40Total: '40HC Total',
          hc45Total: '45HC Total',
          nor20Total: '20NOR Total',
          nor40Total: '40NOR Total',
          typeFcl: 'Sea FCL',
          typeLcl: 'Sea LCL',
          typeAir: 'Air',
          typeRail: 'Rail',
          polSeaport: 'POL',
          podSeaport: 'POD',
          polAirport: 'Origin Airport',
          podAirport: 'Dest Airport',
          polRail: 'Origin Station',
          podRail: 'Dest Station'
        },
        leaveConfirm: {
          title: 'Confirm',
          content: 'Unsaved changes will be lost. Leave anyway?',
          ok: 'Leave',
          cancel: 'Cancel'
        },
        transportModeSwitch: {
          title: 'Switch Transport Mode',
          content:
            'Mode-specific fields will be hidden and restored when switching back. Continue?',
          ok: 'OK',
          cancel: 'Cancel'
        },
        portTypeSwitch: {
          title: 'Switch Address Type',
          content: 'Address-type fields will be hidden and restored when switching back. Continue?',
          ok: 'OK',
          cancel: 'Cancel'
        },
        surchargeSummary: {
          rowTotal: 'Line Total',
          footerLabel: 'Total (counted): ',
          nonContainerTotal: 'Non-container total'
        }
      }
    },

    // Last Mile Rate Management
    lastmile: {
      title: 'Last Mile Rate Management',
      description: 'Manage last mile rate information',
      list: {
        unknown: 'Unknown',
        messages: {
          processingBaseField: 'Processing base data field',
          value: 'Value',
          getSeaportFailed: 'Failed to get seaport base data',
          getWarehouseFailed: 'Failed to get warehouse base data',
          findBaseData: 'Finding base data',
          queryResult: 'Base data query result',
          operationFailed: 'Operation failed'
        }
      },
      regionSelection: {
        title: 'Region',
        detailedAddress: 'Detail Address',
        postalCodeLabel: 'Postal Code:'
      },
      deliveryAddressType: {
        thirdParty: 'Third Party',
        warehouse: 'Warehouse',
        amazonWarehouse: 'Amazon Warehouse',
        easyWarehouse: 'Easy Warehouse',
        unknown: 'Unknown'
      },
      pageTitles: {
        create: 'New Last Mile Rate',
        edit: 'Edit Last Mile Rate',
        detail: 'Last Mile Rate Detail',
        copy: 'Copy Last Mile Rate'
      },
      filters: {
        priceMethod: 'Rate Type',
        status: 'Status',
        destinationPort: 'POD',
        agent: 'Agent Name',
        currency: 'Currency',
        feeName: 'Fee Name',
        unit: 'Unit',
        addressType: 'Address Type',
        country: 'Country',
        postalCode: 'Postal Code',
        state: 'State',
        city: 'City',
        district: 'District',
        warehouseCode: 'Warehouse Code'
      },
      placeholders: {
        priceMethod: 'Select rate type',
        status: 'Select status',
        destinationPort: 'Select POD',
        agent: 'Enter agent name',
        currency: 'Select currency',
        feeName: 'Select fee name',
        unit: 'Select unit',
        addressType: 'Select address type',
        country: 'Select country',
        postalCode: 'Select postal code',
        state: 'Select state',
        city: 'Select city',
        district: 'Select district',
        warehouseCode: 'Enter warehouse code'
      },
      priceMethods: {
        direct: 'Direct',
        branch: 'Branch',
        seaRail: 'Sea-Rail'
      },
      serviceTypes: {
        FCL: 'FCL',
        LCL: 'LCL',
        TRUCK: 'Truck'
      },
      status: {
        draft: 'Draft',
        normal: 'Normal',
        expired: 'Expired',
        offline: 'Offline',
        disabled: 'Disabled',
        pendingImport: 'Pending Import',
        unknown: 'Unknown'
      },
      columns: {
        priceNo: 'Rate No.',
        transportMode: 'Transport Mode',
        pods: 'POD',
        deliveryAddressType: 'Address Type',
        postalCode: 'Postal Code',
        address: 'Address',
        warehouseCode: 'Warehouse Code',
        agentName: 'Agent Name',
        gp20: '20GP',
        gp40: '40GP',
        hc20: '20HC',
        hc40: '40HC',
        hc45: '45HC',
        nor20: '20NOR',
        nor40: '40NOR',
        tk20: '20TK',
        tk40: '40TK',
        ot20: '20OT',
        ot40: '40OT',
        fr20: '20FR',
        fr40: '40FR',
        costPrice: 'Cost',
        sellPrice: 'Selling Price',
        validPeriod: 'Valid Period',
        status: 'Status',
        createdBy: 'Created By',
        dateCreated: 'Create Date',
        modifiedBy: 'Modified By',
        modifyDate: 'Modify Date',
        remarks: 'Remarks',
        actions: 'Actions'
      },
      actions: {
        search: 'Search',
        reset: 'Reset',
        import: 'Import',
        add: 'New Last Mile Rate',
        view: 'Details',
        edit: 'Edit',
        copy: 'Copy',
        more: 'More',
        onShelf: 'Online',
        offShelf: 'Offline',
        delete: 'Delete',
        batchOperations: 'Batch Operations',
        batchOnShelf: 'Batch Online',
        batchOffShelf: 'Batch Offline',
        batchDelete: 'Batch Delete',
        batchPriceChange: 'Batch Price Change',
        aiRecognition: 'AI Recognition',
        batchValidityChange: 'Batch Validity Change',
        booking: 'Book Now',
        rateTrend: 'Rate Trend',
        availableSailings: 'Available Sailings',
        downloadTemplate: 'Download Template'
      },
      messages: {
        loadFailed: 'Load failed',
        loadMoreFailed: 'Load failed, try later',
        featureDeveloping: '{feature} under development...',
        exportFilename: 'Last Mile Rate Data',
        loadDetailFailed: 'Failed to load rate details',
        templateDownloadFailed: 'Failed to download template',
        loadDataFailed: 'Failed to load data',
        deleteConfirm: 'Are you sure you want to delete this rate?',
        deleteSuccess: 'Deleted successfully',
        deleteFailed: 'Failed to delete',
        offShelfSuccess: 'Rate is offline',
        onShelfSuccess: 'Rate is online',
        statusToggleFailed: 'Failed to toggle status'
      },
      aiRecognition: {
        title: 'Last Mile AI Recognition',
        uploadTitle: 'Upload Document',
        uploadDesc: 'PDF, Word, Excel. AI will recognize info.',
        priceTypeName: 'Last Mile Rate'
      },

      // Import
      import: {
        title: 'Import Last Mile Rate',
        breadcrumb: 'Batch Import',
        downloadTemplate: 'Download Last Mile Rate Import Template',
        reminderTitle: 'Instructions',
        reminderLines: [
          '1. Imported rates will have a "Pending Import" status. Please verify them in the list before batch publishing.',
          '2. If the imported data contains duplicate rate records, the system will check against conflict rules.'
        ],
        dragText: 'Click or drag Excel file here to import',
        parsing: 'Parsing Excel...',
        cancel: 'Cancel',
        submit: 'Start Import'
      },
      batch: {
        selected: 'Selected {count} items',
        onShelfConfirm: 'Online {count} rates?',
        offShelfConfirm: 'Offline {count} rates?',
        deleteConfirm: 'Delete {count} rates? Cannot be undone.',
        onShelfSuccess: 'Online {count} rates successful',
        offShelfSuccess: 'Offline {count} rates successful',
        deleteSuccess: 'Delete {count} rates successful',
        priceChange: {
          title: 'Batch Price Change',
          selectedRates: 'Selected {count} rates',
          operationReminder: 'Operation Reminder',
          reminderContent:
            'Proceed with caution. Select specific fee type. Positive integers only.',
          selectCharge: 'Select Fee',
          selectChargeDesc: 'Select fee type, e.g. Last Mile Fee, Delivery Fee, etc.',
          selectChargePlaceholder: 'Select fee type',
          selectPriceMode: 'Select mode',
          priceIncrease: 'Increase',
          priceDecrease: 'Decrease',
          setPriceMode: 'Fixed Amount',
          priceIncreaseDesc: 'Select increase and enter amount',
          priceDecreaseDesc: 'Select decrease and enter amount',
          setPriceDesc: 'Select fixed and enter amount',
          setPrice: 'Set Price',
          selectChargeFirst: 'Select fee type then set price',
          rateListToModify: 'Rates to be modified',
          rateDetailsList: 'Rates list',
          rateNo: 'Rate No.',
          destinationPort: 'POD',
          address: 'Address',
          showChargePrice: '(Showing {charge} price)',
          showAllCharges: '(Showing total price of box fees)',
          noData: 'No Data',
          selectedSummary: 'Selected {count} rates. Modifying {type}.',
          currentChargeType: 'Current fee type:',
          confirmOperation: 'This will modify the',
          confirmOperationSuffix: 'amount.',
          selectChargeAndPrice: 'Set fee and price first.',
          confirm: 'Confirm',
          cancel: 'Cancel',
          batchPriceSuccess: 'Batch price successful',
          batchPriceFailed: 'Batch price failed',
          batchPriceError: 'Batch price error',
          chargeItemRequired: 'Select fee type',
          priceInputRequired: 'Enter at least one adjustment amount',
          pricePlaceholders: {
            increase: 'Increase amount',
            decrease: 'Decrease amount',
            setPrice: 'Fixed price',
            default: 'Enter price'
          }
        },
        timeChange: {
          title: 'Batch Time Change',
          operationAlert: 'Proceed with caution.',
          etdTab: 'ETD',
          etaTab: 'ETA',
          validDateTab: 'Validity',
          setNewEtd: 'Set new ETD',
          setNewEta: 'Set new ETA',
          setNewValidDate: 'Set new validity',
          newEtd: 'New ETD:',
          newEta: 'New ETA:',
          validPeriod: 'Validity:',
          selectEtd: 'Select ETD',
          selectEta: 'Select ETA',
          startDate: 'Start Date',
          endDate: 'End Date',
          currentEtd: 'Current ETD',
          currentEta: 'Current ETA',
          currentValidPeriod: 'Current Validity',
          currentValidDate: 'Current Validity',
          rateListToModify: 'Rates list',
          selectedSummary: 'Selected {count} rates. Modifying {type}.',
          cancel: 'Cancel',
          confirm: 'Confirm',
          etd: 'ETD',
          eta: 'ETA',
          validDate: 'Validity',
          operationReminder: 'Operation Reminder',
          reminderContent: 'This will modify time info for selected rates.',
          setNewEtdTitle: 'Set New ETD',
          setNewEtaTitle: 'Set New ETA',
          setNewValidDateTitle: 'Set New Validity',
          etdLabel: 'ETD',
          etaLabel: 'ETA',
          validDateLabel: 'Validity',
          selectEtdPlaceholder: 'Select ETD',
          selectEtaPlaceholder: 'Select ETA',
          startDatePlaceholder: 'Start Date',
          endDatePlaceholder: 'End Date',
          selectedRatesList: 'Rates list',
          tipText: 'Confirm data accuracy.',
          etdUpdateSuccess: 'ETD updated for {count} rates',
          etaUpdateSuccess: 'ETA updated for {count} rates',
          validDateUpdateSuccess: 'Validity updated for {count} rates',
          updateFailed: 'Batch time failed',
          selectValidDateRange: 'Select full validity range',
          startDateMustBeforeEndDate: 'Start must be before End'
        },
        validityChange: {
          title: 'Batch Validity Change',
          validityPeriod: 'Validity',
          selectValidityPeriod: 'Select validity',
          startDate: 'Start Date',
          endDate: 'End Date'
        },
        confirm: {
          online: 'Confirm Online',
          offline: 'Confirm Offline',
          delete: 'Confirm Delete',
          onlineContent: 'Online {count} rates?',
          offlineContent: 'Offline {count} rates?',
          deleteContent: 'Delete {count} rates? Cannot be undone.'
        },
        messages: {
          onlineSuccess: 'Batch online successful',
          onlineFailed: 'Batch online failed',
          offlineSuccess: 'Batch offline successful',
          offlineFailed: 'Batch offline failed',
          batchDeleteSuccess: 'Batch delete successful',
          batchDeleteFailed: 'Batch delete failed',
          validityChangeSuccess: 'Batch validity successful',
          validityChangeFailed: 'Batch validity failed',
          selectRatesFirst: 'Select rates first',
          selectRatesToOnline: 'Select rates to online first',
          selectRatesToOffline: 'Select rates to offline first',
          selectRatesToModify: 'Select rates to modify first',
          selectRatesToDelete: 'Select rates to delete first',
          onlyDraftCanOnline: 'Only Draft can be online. Selection: {status}',
          onlyNormalCanOffline: 'Only Published can be offline. Selection: {status}',
          onlyDraftCanModify: 'Only Draft can be modified',
          onlyDraftCanDelete: 'Only Draft can be deleted',
          loadDataFailed: 'Load failed',
          loadDetailFailed: 'Load detail failed',
          loadDataError: 'Load last mile data failed:',
          copySuccess: 'Rate info copied. Rate No. will be regenerated. Confirm and save.',
          importPending: 'Importing under development',
          templateDownloadFailed: 'Template download failed',
          onShelfSuccess: 'Rate is online',
          offShelfSuccess: 'Rate is offline',
          statusToggleFailed: 'Status toggle failed',
          deleteConfirm: 'Are you sure to delete this rate?',
          deleteSuccess: 'Delete successful',
          deleteFailed: 'Delete failed',
          totalCount: 'Total {count} items',
          unknown: 'Unknown',
          exportFilename: 'Last Mile Rate Data',
          processingBaseField: 'Processing base field',
          value: 'Value',
          getSeaportFailed: 'Get port data failed',
          getWarehouseFailed: 'Get warehouse data failed',
          findBaseData: 'Find base data',
          queryResult: 'Query Result'
        }
      },
      chargeItems: {
        lastmileFee: 'Last Mile Fee',
        handlingFee: 'Handling Fee',
        documentFee: 'Documentation Fee',
        deliveryFee: 'Delivery Fee'
      },
      batchPrice: {
        title: 'Batch Price Change',
        chargeItemRequired: 'Select fee type',
        priceInputRequired: 'Enter at least one box type price',
        priceUpdateFailed: 'Batch price failed',
        setPriceSuccess: 'Fixed price set for {count} rates',
        increaseSuccess: 'Increased {count} rates',
        decreaseSuccess: 'Decreased {count} rates',
        pricePlaceholders: {
          increase: 'Increase amount',
          decrease: 'Decrease amount',
          setPrice: 'Fixed price',
          default: 'Enter price'
        }
      },
      detail: {
        exportColumns: {
          warehouseCode: 'Warehouse Code',
          priceNo: 'Lastmile Rate No.',
          pods: 'Destination Port',
          deliveryAddressType: 'Address Type',
          postalCode: 'Postal Code',
          address: 'Address',
          agentName: 'Agent Name',
          gp20Total: '20GP',
          gp40Total: '40GP',
          hc40Total: '40HC',
          nor40Total: '40NOR',
          hc45Total: '45HC',
          beginDate: 'Effective Date',
          endDate: 'Expiry Date',
          status: 'Status',
          createdBy: 'Created By',
          createdTime: 'Created Time',
          modifiedBy: 'Modified By',
          updatedTime: 'Modified Time',
          remarks: 'Remarks'
        },
        // Sections
        sections: {
          basicInfo: 'Basic Information',
          pickupAddress: 'Pickup Address',
          destinationInfo: 'Destination Info',
          deliveryAddress: 'Delivery Address',
          transportConditions: 'Transport Conditions (Barge/Rail)',
          regionSelection: 'Region Selection',
          rateInfo: 'Fee Details'
        },

        // Box Settings
        boxTypeSettings: {
          title: 'Box Settings',
          selectBoxTypes: 'Select box types to display:',
          updateSuccess: 'Box settings updated'
        },

        // Page Actions
        pageActions: {
          back: 'Back',
          saveDraft: 'Save Draft',
          saveAndPublish: 'Online Directly',
          edit: 'Edit',
          copy: 'Copy',
          copyAlert: 'Rate info copied. No. will be regenerated. Confirm and save.'
        },

        // Basic Info
        basicInfo: {
          title: 'Basic Information',
          priceNo: 'Rate No.',
          priceNoPlaceholder: 'Auto generated, e.g. LMRYYMMDD0001',
          priceNoCopyPlaceholder: 'Regenerated after copying, e.g. LMRYYMMDD0001',
          priceMethod: 'Rate Type',
          priceMethodRequired: 'Select rate type',
          priceMethodPlaceholder: 'Select rate type',
          destinationPort: 'POD',
          destinationPortRequired: 'Select POD',
          destinationPortPlaceholder: 'Select POD',
          agent: 'Agent Name',
          agentRequired: 'Enter agent name',
          agentPlaceholder: 'Enter agent name',
          validPeriod: 'Valid Period',
          validPeriodRequired: 'Select validity',
          validPeriodStart: 'Start Date',
          validPeriodEnd: 'End Date'
        },

        // Delivery Address Choice
        regionSelection: {
          title: 'Delivery Address Choice',
          aiRecognition: 'AI Recognition',
          addRegion: 'Add Delivery Address',
          region: 'Address {index}',
          atLeastOneRegion: 'At least one address required',
          administrativeDivision: 'Admin Division',
          detailedLocation: 'Detailed Location',
          detailedAddress: 'Detailed Address',
          detailAddress: 'Detailed Address',
          enterDetailedAddress: 'Enter detailed address',
          noRegionSet: 'No region set',
          clickToExpand: 'Click to expand settings',
          country: 'Country',
          state: 'Province/State',
          city: 'City',
          district: 'District/County',
          street: 'Township/Street',
          community: 'Community',
          deliveryAddress: 'Delivery Address',
          addressType: 'Address Type',
          thirdPartyAddress: 'Third Party',
          amazonWarehouse: 'Amazon Warehouse',
          easyWarehouse: 'Easy Warehouse',
          notSelected: 'Not Selected',
          postalCode: 'Postal Code',
          selectCountry: 'Select country',
          enterPostalCode: 'Enter postal code (optional)',
          selectProvince: 'Select province/state',
          selectCity: 'Select city',
          selectDistrict: 'Select district/county',
          selectStreet: 'Select township/street',
          selectCommunity: 'Select community',
          warehouseCode: 'Warehouse Code',
          easyWarehouseCode: 'Easy Warehouse Code',
          destinationPort: 'POD',
          selectDestinationPort: 'Select POD',
          multiplePortsTip: 'Tip: Multiple pods can be selected if rates are same',
          agent: 'Agent',
          enterAgentName: 'Enter agent name',
          etd: 'ETD',
          selectEtd: 'Select ETD',
          eta: 'ETA',
          selectEta: 'Select ETA',
          validPeriod: 'Validity',
          startDate: 'Start Date',
          endDate: 'End Date',
          remarks: 'Remarks',
          enterRemarks: 'Enter remarks',
          serviceType: 'Service Type',
          selectServiceType: 'Select service type',
          onePrice: 'One Price',
          selectOnePrice: 'Select one price',
          receivingInstructions: 'Receiving Instructions',
          enterReceivingInstructions: 'Enter receiving instructions',
          selectAmazonWarehouse: 'Select Amazon Warehouse',
          selectEasyWarehouse: 'Select Easy Warehouse'
        },

        // Rate Details
        rateDetails: {
          title: 'Rate Details',
          containerRate: 'Box-based',
          nonContainerRate: 'Non-box-based',
          addContainerRate: 'Add Box-based',
          addNonContainerRate: 'Add Non-box-based',
          atLeastOneRateItem: 'At least one item required',
          feeName: 'Fee Name',
          feeNamePlaceholder: 'Select fee name',
          currency: 'Currency',
          currencyPlaceholder: 'Select currency',
          unit: 'Unit',
          unitPlaceholder: 'Select unit',
          price: 'Price',
          pricePlaceholder: 'Enter',
          priceInquiry: 'Inquiry',
          specialNote: 'Special Note',
          specialNotePlaceholder: 'Note',
          actions: 'Actions'
        },

        // Page Titles
        pageTitles: {
          copyRate: 'Copy Last Mile Rate',
          createRate: 'New Last Mile Rate',
          editRate: 'Edit Last Mile Rate',
          rateDetail: 'Last Mile Rate Detail',
          copyLastmileRate: 'Copy Last Mile Rate',
          addLastmileRate: 'New Last Mile Rate',
          editLastmileRate: 'Edit Last Mile Rate',
          lastmileRateDetail: 'Last Mile Rate Detail'
        },

        // Rate Information
        rateInfo: {
          containerRateProjects: 'Basic Freight',
          nonContainerRateProjects: 'Surcharge',
          noData: 'No Data',
          selectBillingUnit: 'Select billing unit',
          enterUnitPrice: 'Enter unit price',
          boxTypeSettings: 'Box Settings',
          addProject: 'Add Project',
          selectFeeName: 'Select Fee Name',
          autoFill: 'Auto Fill',
          enterPrice: 'Enter Price',
          delete: 'Delete',
          basicFee: 'Basic Fee',
          specialNote: 'Remarks',
          unitContainer: 'Container',
          paymentPrepaid: 'Prepaid',
          paymentCollect: 'Collect',
          selectPaymentType: 'Please select',
          freightFeeName: 'Freight',
          lastMileSurchargeType: 'Last-mile Surcharge',
          feeSourceManual: 'Manual',
          feeSourceSystem: 'System Rule',
          locked: 'Locked',
          columns: {
            feeName: 'Fee Name',
            nameEn: 'English Name',
            feeType: 'Fee Type',
            currency: 'Currency',
            billingUnit: 'Unit',
            unitPrice: 'Unit Price',
            specialNote: 'Remarks',
            paymentType: 'Payment Terms',
            feeSource: 'Fee Source',
            actions: 'Actions'
          }
        },

        // Modals
        modals: {
          containerSettings: 'Box Settings',
          selectBoxTypes: 'Select box types to display'
        },

        // Form Fields
        formFields: {
          deliveryAddressSelection: 'Delivery Address Selection',
          addAddress: 'Add Address'
        },

        // Buttons
        buttons: {
          back: 'Back',
          saveDraft: 'Save Draft',
          directOnline: 'Direct Online',
          save: 'Save',
          edit: 'Edit',
          copy: 'Copy'
        },

        // Messages
        messages: {
          copyingRateInfo: 'Copying rate info. Rate No. will be regenerated. Confirm and save.',
          callForQuote: 'Inquiry',
          boxTypeSettingsUpdated: 'Box settings updated',
          dataLoadFailed: 'Load failed: {error}',
          unknownError: 'Unknown error',
          copyDataLoadFailed: 'Copy load failed',
          copiedToNewPage: 'Data copied. Confirm and save.'
        },

        // Validation
        validation: {
          podsRequired: 'Select at least one POD',
          agentRequired: 'Enter agent name',
          validPeriodRequired: 'Select validity',
          regionRequired: 'At least one address required',
          rateItemRequired: 'At least one item required',
          priceMethodRequired: 'Select rate type',
          destinationPortRequired: 'Select POD',
          feeNameRequired: 'Select fee name',
          currencyRequired: 'Select currency',
          unitRequired: 'Select unit',
          priceRequired: 'Enter price',
          atLeastOneRegionRequired: 'At least one delivery address required',
          freightCurrencyRequired: 'Basic freight row {row}: select currency',
          freightBoxPriceRequired:
            'Basic freight row {row}: enter at least one box price when unit is container',
          freightUnitPriceRequired: 'Basic freight row {row}: enter unit price',
          surchargeFeeNameRequired: 'Surcharge row {row}: select fee name',
          surchargeUnitRequired: 'Surcharge row {row}: select unit',
          surchargeCurrencyRequired: 'Surcharge row {row}: select currency',
          surchargeBoxPriceRequired:
            'Surcharge row {row}: enter at least one box price when unit is container',
          surchargeUnitPriceRequired: 'Surcharge row {row}: enter unit price'
        },

        // Hazard classes
        hazardClasses: {
          class1: 'Class 1 Explosives',
          class2: 'Class 2 Gases',
          class3: 'Class 3 Flammable liquids',
          class4: 'Class 4 Flammable solids',
          class5: 'Class 5 Oxidizing substances',
          class6: 'Class 6 Toxic substances',
          class7: 'Class 7 Radioactive material',
          class8: 'Class 8 Corrosives',
          class9: 'Class 9 Miscellaneous'
        },

        // Save Messages
        saveMessages: {
          updateSuccess: 'Rate updated and published!',
          createSuccess: 'Rate created and published!',
          saveFailed: 'Save failed: ',
          unknownError: 'Unknown error',
          checkRequired: 'Check required fields',
          draftSaveSuccess: 'Draft saved',
          draftSaveFailed: 'Draft save failed: ',
          onlyDraftCanSave: 'Only drafts can be saved',
          onlyDraftCanSaveDraft: 'Only drafts can be saved as draft',
          onlyDraftCanEdit: 'Only Draft can be edited'
        },

        // Data Loading
        dataLoading: {
          loadFailed: 'Load failed: ',
          copyLoadFailed: 'Copy load failed: ',
          loadFeeDataFailed: 'Load fee data failed',
          noBasicFeeFound: 'No basic fee found, select manually',
          loadBasicFeeFailed: 'Load basic fee failed'
        },

        // [CCTOWER-2469] leave / transport switch / surcharge / field labels
        leaveConfirm: {
          title: 'Confirm',
          content: 'Unsaved changes will be lost. Leave anyway?',
          ok: 'Leave',
          cancel: 'Cancel'
        },
        transportModeSwitch: {
          title: 'Switch Transport Mode',
          content:
            'Mode-specific fields will be hidden and restored when switching back. Continue?',
          ok: 'OK',
          cancel: 'Cancel'
        },
        portTypeSwitch: {
          title: 'Switch Address Type',
          content: 'Address-type fields will be hidden and restored when switching back. Continue?',
          ok: 'OK',
          cancel: 'Cancel'
        },
        addressTypeSwitch: {
          title: 'Switch Delivery Address Type',
          content: 'Address-type fields will be hidden and restored when switching back. Continue?',
          ok: 'OK',
          cancel: 'Cancel'
        },
        surchargeSummary: {
          rowTotal: 'Line Total',
          footerLabel: 'Total (counted): ',
          nonContainerTotal: 'Non-container total'
        },
        fields: {
          officeIds: 'Company',
          officeIdsRequired: 'Select company',
          officeIdsPlaceholder: 'Select company',
          supplierId: 'Supplier',
          supplierIdPlaceholder: 'Select supplier',
          cargoType: 'Cargo Type',
          cargoTypeRequired: 'Select cargo type',
          cargoTypePlaceholder: 'Select cargo type',
          cargoTypeGeneral: 'General',
          cargoTypeDangerous: 'Dangerous',
          cargoTypeCold: 'Cold Chain',
          cargoTypeSpecial: 'Special',
          hazardTitle: 'Dangerous Goods',
          unNo: 'UN No.',
          unNoRequired: 'UN No. required for dangerous goods',
          unNoPlaceholder: 'Enter 4 digits',
          unNoTip: '4-digit numeric format',
          unNoFormat: 'UN No. must be 4 digits',
          hazardClass: 'Class',
          hazardClassRequired: 'Class required for dangerous goods',
          hazardClassPlaceholder: 'Select hazard class',
          cargoDescription: 'Commodity',
          cargoDescriptionRequired: 'Commodity required for dangerous goods',
          cargoDescriptionPlaceholder: 'Enter commodity',
          hsCode: 'HSCODE',
          hsCodeRequired: 'HSCODE required for dangerous goods',
          hsCodePlaceholder: 'Enter 10-digit HSCODE',
          hsCodeTip: 'Standard 10-digit code',
          hsCodeFormat: 'HSCODE must be 10 characters',
          weightLimit: 'Weight Limit (KG)',
          weightLimitPlaceholder: 'Enter weight limit',
          freeBoxDays: 'Free Days',
          freeBoxDaysPlaceholder: 'Enter days',
          voyageDays: 'Voyage Days',
          voyageDaysPlaceholder: 'Enter voyage days',
          containerOwnership: 'Ownership',
          containerOwnershipRequired: 'Select ownership',
          containerOwnershipPlaceholder: 'Select',
          specialPriceStatus: 'Special Price Status',
          specialPriceStatusRequired: 'Select special price status',
          specialPriceStatusDisallow: 'Special price not allowed',
          specialPriceStatusAllow: 'Special price allowed',
          specialPriceStatusSpecial: 'Special price',
          transportMode: 'Transport Mode',
          transportModeRequired: 'Select transport mode',
          transportModeTruck: 'Truck',
          transportModeBarge: 'Barge',
          transportModeRail: 'Rail',
          bargeTitle: 'Barge Info',
          bargeCarrierId: 'Carrier',
          bargeCarrierIdRequired: 'Select carrier',
          bargeCarrierIdPlaceholder: 'Select carrier',
          loadingPortId: 'Destination Port',
          loadingPortIdRequired: 'Select destination port',
          loadingPortIdPlaceholder: 'Select destination port',
          destWharfId: 'Destination Terminal',
          destWharfIdPlaceholder: 'Select destination terminal (optional)',
          bargeName: 'Barge Name',
          bargeNamePlaceholder: 'Enter vessel name',
          bargeVoyage: 'Barge Voyage',
          bargeVoyagePlaceholder: 'Enter voyage',
          bargeEtd: 'Barge Departure Time',
          bargeEta: 'Barge Arrival Time',
          bargeDatePlaceholder: 'Select date',
          bargeVoyageDays: 'Barge Voyage Days',
          bargeVoyageDaysPlaceholder: 'Enter voyage days',
          railTitle: 'Rail Info',
          railCompanyId: 'Rail Company',
          railCompanyIdRequired: 'Select rail company',
          railCompanyIdPlaceholder: 'Select rail company',
          loadingStationId: 'Destination Station',
          loadingStationIdRequired: 'Select destination station',
          loadingStationIdPlaceholder: 'Select destination station',
          railLineType: 'Line Type',
          railLineTypePlaceholder: 'Enter line type',
          railTrainNo: 'Train No.',
          railTrainNoPlaceholder: 'Enter train No.',
          railDepartureTime: 'Departure Time',
          railDepartureTimePlaceholder: 'Select departure time',
          portType: 'Address Type',
          portTypeRequired: 'Select address type',
          portTypeSeaport: 'Seaport',
          portTypeAirport: 'Airport',
          portTypeRailway: 'Railway Station',
          pods: 'Discharge Port',
          podsRequired: 'Select discharge port',
          podsPlaceholder: 'Select discharge port',
          wharfId: 'Discharge Terminal',
          wharfIdPlaceholder: 'Select discharge terminal',
          departureStationId: 'Discharge Station',
          departureStationIdRequired: 'Select discharge station',
          departureStationIdPlaceholder: 'Select discharge station',
          airportId: 'Discharge Airport',
          airportIdRequired: 'Select discharge airport',
          airportIdPlaceholder: 'Select discharge airport',
          airportTerminal: 'Airport Terminal',
          airportTerminalPlaceholder: 'Enter airport terminal (optional)',
          minCharge: 'Min Charge',
          maxCharge: 'Max Charge',
          isCountTotal: 'Count in Total',
          isTotalIncluded: 'Included in Total'
        },
        // [CCTOWER-2469] trunk match
        trunkMatch: {
          title: 'Match Trunk Rate',
          staleAlert: 'Match criteria changed. Please refresh.',
          refresh: 'Refresh Match',
          manual: 'Manual Match',
          selectTitle: 'Select Trunk Rate',
          confirm: 'OK',
          cancel: 'Cancel',
          empty: 'No trunk rate matched yet',
          notFound: 'No matching trunk rate found',
          multiCandidates: 'Found {count} candidates. Please select one.',
          matchedSuccess: 'Trunk rate matched',
          selectedSuccess: 'Trunk rate selected',
          needPortType: 'Please select address type first',
          needPod: 'Please select discharge port first',
          needAirport: 'Please select airport first',
          needRailStation: 'Please select discharge station first',
          needSelectCandidate: 'Please select a trunk rate',
          typeLabel: 'Type: ',
          priceNoLabel: 'No.: ',
          officeLabel: 'Office: ',
          supplierLabel: 'Supplier: ',
          periodLabel: 'Validity: ',
          combinedPriceLabel: 'Combined: ',
          trunkType: 'Trunk Type',
          priceNo: 'Rate No.',
          office: 'Office',
          supplier: 'Supplier',
          validPeriod: 'Validity',
          matchedAt: 'Matched At',
          polNode: 'Origin',
          podNode: 'Destination',
          spaceStatus: 'Space Status',
          priceTrend: 'Price Trend',
          weekdays: 'Schedule',
          detentionFreeDays: 'Free Detention',
          voyageDays: 'Transit Days',
          actions: 'Actions',
          detail: 'Details',
          detailTitle: 'Trunk Rate Details',
          openRateDetail: 'Open Rate Detail',
          railDetailUnavailable: 'This trunk type does not support opening the rate detail page',
          inquiry: 'Inquiry',
          combinedBox: 'Last Mile {pre} + Trunk {trunk} = {total}',
          gp20Total: '20GP Total',
          gp40Total: '40GP Total',
          hc40Total: '40HC Total',
          hc45Total: '45HC Total',
          nor20Total: '20NOR Total',
          nor40Total: '40NOR Total',
          typeFcl: 'Sea FCL',
          typeLcl: 'Sea LCL',
          typeAir: 'Air',
          typeRail: 'Rail',
          polSeaport: 'POL',
          podSeaport: 'POD',
          polAirport: 'Origin Airport',
          podAirport: 'Dest Airport',
          polRail: 'Origin Station',
          podRail: 'Dest Station'
        }
      }
    }
  }
}
